<template>
    <div class="configuration-page">
        <b-overlay :show="loading" :rounded="true" no-center>
            <div class="d-flex h-100">
                <!-- Sidebar -->
                <div class="config-sidebar-col">
                    <div class="config-sidebar">
                        <!-- Sticky Header Container -->
                        <div class="sidebar-header-container glass-pane">
                            <!-- Page Title -->
                            <h6 class="sidebar-title">Configuration</h6>

                            <!-- Search Field -->
                            <div class="search-section">
                                <div class="d-flex align-items-center" style="gap: 7px;">
                                    <label for="config-filter" class="m-0"><i class="fa fa-search" /></label>
                                    <b-input id="config-filter" v-model="query" type="search" placeholder="Filter Options" debounce="300" />
                                </div>
                            </div>
                        </div>

                        <!-- Groups Navigation -->
                        <div class="groups-section">
                            <!-- All Groups Option -->
                            <div
                                class="group-item"
                                :class="{ 'active': isAllGroupsSelected }"
                                @click="selectAllGroups"
                            >
                                <i class="fa fa-list-ul fa-fw"></i>
                                <span class="group-name">All Groups</span>
                                <b-badge variant="primary" pill>{{ configKeys.length }}</b-badge>
                            </div>

                            <!-- Individual Groups -->
                            <div
                                v-for="group in configurationGroups"
                                :key="group"
                                class="group-item"
                                :class="{ 'active': selectedGroup === group }"
                                @click="selectGroup(group)"
                            >
                                <i class="fa fa-folder fa-fw"></i>
                                <span class="group-name">{{ group }}</span>
                                <b-badge v-if="getFieldCountForGroup(group)" variant="primary" pill>{{ getFieldCountForGroup(group) }}</b-badge>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Main Content -->
                <div class="main-content-col">
                    <div class="config-content">
                        <!-- Top Search Section (shown when sidebar is hidden) -->
                        <div class="top-search-section glass-pane">
                            <div class="top-search-grid">
                                <div class="group-select">
                                    <div class="d-flex align-items-center" style="gap: 7px;">
                                        <label for="config-group-select" class="m-0"><i class="fa fa-folder" /></label>
                                        <b-form-select id="config-group-select" v-model="selectedGroup" :options="groupSelectOptions" />
                                    </div>
                                </div>
                                <div class="search-field">
                                    <div class="d-flex align-items-center" style="gap: 7px;">
                                        <label for="config-filter-top" class="m-0"><i class="fa fa-search" /></label>
                                        <b-input id="config-filter-top" v-model="query" type="search" placeholder="Filter Options" debounce="300" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- All Groups View -->
                        <div v-if="isAllGroupsSelected">
                            <div v-for="group in displayedGroups" :key="group">
                                <h5 class="glass-pane group-header collapsible" @click="toggleGroup(group)">
                                    <i class="fa fa-fw" :class="isFiltering || openGroups.includes(group) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                    {{ group }}
                                </h5>
                                <div class="group-content" v-if="isFiltering || openGroups.includes(group)">
                                    <div class="d-flex flex-column" style="gap: 12px;">
                                        <div class="grid">
                                            <div v-for="name in getKeysForGroup(group, false)" :key="name">
                                                <ConfigurationField v-model="getConfigValue(name).value" @input="setConfigValue(name, $event)" :label="formatConfigName(name)"
                                                    :type="getConfigType(name)" :help="fields[name]?.help"
                                                    :add-label="fields[name].addLabel" :locked-ids="fields[name].lockedIds"
                                                    :options="fields[name]?.options" :groups="fields[name]?.serviceGroups" :append="fields[name]?.append"
                                                    :maxlength="fields[name].maxlength" :min="fields[name].min"
                                                    :max="fields[name].max" :step="fields[name]?.step" :reorderable="fields[name]?.reorderable"
                                                    :default-all-checked="fields[name]?.defaultAllChecked"
                                                    :disabled-groups="getDisabledGroups(name)" />
                                            </div>
                                        </div>
                                        <div v-for="name in getKeysForGroup(group, true)" :key="name">
                                            <ConfigurationField v-model="getConfigValue(name).value" @input="setConfigValue(name, $event)" :label="formatConfigName(name)"
                                                :type="getConfigType(name)" :help="fields[name]?.help"
                                                :add-label="fields[name].addLabel" :locked-ids="fields[name].lockedIds"
                                                :append="fields[name]?.append" bold-label :max-length="fields[name].maxlength"
                                                :min="fields[name].min" :max="fields[name].max" :step="fields[name]?.step"
                                                :reorderable="fields[name]?.reorderable" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Single Group View -->
                        <div v-else-if="selectedGroup">
                            <h5 class="glass-pane group-header">{{ selectedGroup }}</h5>
                            <div class="group-content">
                                <div class="d-flex flex-column" style="gap: 12px;">
                                    <div class="grid">
                                        <div v-for="name in getKeysForGroup(selectedGroup, false)" :key="name">
                                            <ConfigurationField v-model="getConfigValue(name).value" @input="setConfigValue(name, $event)" :label="formatConfigName(name)"
                                                :type="getConfigType(name)" :help="fields[name]?.help"
                                                :add-label="fields[name].addLabel" :locked-ids="fields[name].lockedIds"
                                                :options="fields[name]?.options" :groups="fields[name]?.serviceGroups" :append="fields[name]?.append"
                                                :maxlength="fields[name].maxlength" :min="fields[name].min"
                                                :max="fields[name].max" :step="fields[name]?.step" :reorderable="fields[name]?.reorderable"
                                                :default-all-checked="fields[name]?.defaultAllChecked"
                                                :disabled-groups="getDisabledGroups(name)" />
                                        </div>
                                    </div>
                                    <div v-for="name in getKeysForGroup(selectedGroup, true)" :key="name">
                                        <ConfigurationField v-model="getConfigValue(name).value" @input="setConfigValue(name, $event)" :label="formatConfigName(name)"
                                            :type="getConfigType(name)" :help="fields[name]?.help"
                                            :add-label="fields[name].addLabel" :locked-ids="fields[name].lockedIds"
                                            :append="fields[name]?.append" bold-label :max-length="fields[name].maxlength"
                                            :min="fields[name].min" :max="fields[name].max" :step="fields[name]?.step"
                                            :reorderable="fields[name]?.reorderable" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="actions glass-pane d-flex align-items-center">
                            <div class="flex-fill">
                                <b-dropdown v-if="canSave" text="Tools" variant="primary" size="sm" dropup>
                                    <b-dropdown-item @click="showExportModal = true">
                                        <i class="fa fa-download mr-2" />Export Configuration
                                    </b-dropdown-item>
                                    <b-dropdown-item @click="showImportModal = true">
                                        <i class="fa fa-upload mr-2" />Import Configuration
                                    </b-dropdown-item>
                                </b-dropdown>
                            </div>
                            <div>
                                <b-button variant="success" @click="saveConfiguration" :disabled="!canSave" size="lg">
                                    Save Configuration
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Patient App Delivery Survey Question Sets Modal -->
            <b-modal id="patientAppDeliverySurveyQuestionSetsModal" hide-footer no-fade
                title="Patient App Post-Delivery Survey Question Sets" size="lg"
                @hidden="fetchPatientAppDeliverySurveyQuestionSets()">
                <TaskQuestionSets :entity-type="EntityType.PatientAppDeliverySurvey" />
            </b-modal>

            <!-- Export Modal -->
            <b-modal v-model="showExportModal" title="Export Configuration" @ok="exportConfiguration" ok-title="Export"
                :ok-disabled="exporting">
                <p>Export the current configuration settings to a JSON file.</p>
                <b-form-group label="Description (optional)" label-for="export-description">
                    <b-form-input id="export-description" v-model="exportDescription"
                        placeholder="e.g., Baseline production config" />
                </b-form-group>
                <small class="text-muted">
                    The exported file can be imported into another instance or used to restore configuration later.
                </small>
            </b-modal>

            <!-- Import Modal -->
            <b-modal v-model="showImportModal" title="Import Configuration" size="xl" @hidden="resetImport"
                :ok-disabled="!canApplyImport || importing" :ok-title="importing ? 'Importing...' : 'Apply Import'"
                @ok="applyImport">
                <div v-if="!importPreview">
                    <p>Select a configuration export file to import.</p>
                    <b-form-group label="Configuration File" label-for="import-file">
                        <b-form-file id="import-file" v-model="importFile" accept=".json"
                            placeholder="Choose a file or drop it here..." drop-placeholder="Drop file here..."
                            @input="previewImport" />
                    </b-form-group>
                    <b-alert v-if="importError" show variant="danger">{{ importError }}</b-alert>
                </div>

                <div v-else>
                    <ConfigurationImportPreview :preview="importPreview" :metadata="importData?.metadata"
                        :available-groups="configurationGroups" v-model="selectedGroupsToImport" />
                </div>
            </b-modal>
        </b-overlay>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import axios from "axios";
import { Configuration, autoRefillCadenceFields } from "@/models/Configuration";
import { PatientAddressType } from "@/models/Patient";
import { ServiceTypeGroups, ServiceAddOnGroups } from "@/models/Shipping/ShippingServiceCatalog";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import ConfigurationField from "@/components/Configuration/ConfigurationField.vue";
import ConfigurationImportPreview from "@/components/Configuration/ConfigurationImportPreview.vue";
import { UserTask } from "@/models/UserTask";
import { EntityType } from "@/models/EntityType";
import TaskQuestionSets from "@/components/Tasks/TaskQuestionSets.vue";
import {
    ConfigurationExport,
    ConfigurationImportPreview as ImportPreviewType,
    ConfigurationImportRequest,
} from "@/models/ConfigurationExport";

const entityName = "Configuration";

// Stops at 28 so the cycle lands in every month: the AR workers fire on exact day equality, so a
// 29-31 closing day would silently skip February. The leading blank is the documented off-switch.
const arStatementClosingDayOptions = [
    { value: null, text: "" },
    ...Array.from({ length: 28 }, (_, i) => ({ value: i + 1, text: `${i + 1}` })),
];

@Component({
    name: "ConfigurationPage",
    components: {
        ConfigurationField,
        ConfigurationImportPreview,
        TaskQuestionSets,
    },
})
export default class ConfigurationPage extends Vue {
    EntityType = EntityType;

    protected configuration: any = new Configuration();
    protected loading = false;

    // arGateOpen as last persisted, so the post-save advisory only fires when the save itself
    // opened the gate rather than on every save. Refresh wherever configuration is replaced.
    protected arGateWasOpen = false;
    protected taskTemplates: UserTask[] = [];
    protected patientAppDeliverySurveyQuestionSets: { id: number; name: string }[] = [];
    protected patientAppPriceSchedules: { id: number; name: string }[] = [];

    // Export/Import state
    protected showExportModal = false;
    protected showImportModal = false;
    protected exportDescription = "";
    protected exporting = false;
    protected importing = false;
    protected importFile: File | null = null;
    protected importData: ConfigurationExport | null = null;
    protected importPreview: ImportPreviewType | null = null;
    protected importError = "";
    protected selectedGroupsToImport: string[] = [];

    get fields(): any {
        return {
            enableAR: {
                title: "Enable AR / Patient Billing",
                groups: ["AR / Patient Billing"],
                help: "Master toggle for the Accounts Receivable / patient on-account billing system. When off, no AR features appear anywhere in the application except this configuration section.",
            },
            arDefaultCreditLimit: {
                title: "Default Credit Limit",
                groups: ["AR / Patient Billing"],
                type: "number",
                min: 0,
                append: "$",
                help: "Default credit limit applied to new AR accounts. Leave blank for no default limit. Individual accounts can override this.",
            },
            arStatementClosingDay: {
                title: "Statement Closing Day",
                groups: ["AR / Patient Billing"],
                type: "select",
                options: arStatementClosingDayOptions,
                help: "Day of the month the monthly AR statement cycle closes, pharmacy-wide. Defaults to the 1st when AR is first enabled. Leave blank to stop generating statements and auto-payments.",
            },
            arAgingBucketDays: {
                title: "Aging Bucket Thresholds (Days)",
                groups: ["AR / Patient Billing"],
                type: "number-array",
                help: "Day thresholds that define AR aging buckets. Default 30, 60, 90, 120 produces Current, 31-60, 61-90, 91-120 and 120+ buckets.",
            },
            arDefaultStatementDeliveryMethods: {
                title: "Default Statement Delivery Methods",
                groups: ["AR / Patient Billing"],
                type: "checkbox-array",
                help: "Default channel(s) used to deliver statements for new AR accounts. Individual accounts can override this.",
                options: [
                    { value: 1, text: "Printed / Mailed" },
                    { value: 2, text: "Email" },
                    { value: 4, text: "Patient Portal Only" },
                    { value: 8, text: "SMS with Portal Link" },
                ],
            },
            arPortalPaymentCC: {
                title: "Allow Credit Card Payments in Patient Portal",
                groups: ["AR / Patient Billing"],
                help: "When enabled, patients can pay their AR balance by credit card from the patient portal and mobile app.",
            },
            enableArAutoPay: {
                title: "Allow Auto-Pay Enrollment",
                groups: ["AR / Patient Billing"],
                help: "When enabled, the account's responsible party can enroll in auto-pay from the patient portal and mobile app; enrolled accounts are charged automatically on each statement closing day.",
            },
            arStatementReasonCodes: {
                title: "Statement Reason / Credit Codes",
                groups: ["AR / Patient Billing"],
                type: "string-array",
                maxlength: 50,
                help: "Configurable reason/credit codes selectable when posting AR adjustments and credits.",
            },
            arRequireReasonCode: {
                title: "Require a Reason Code on Adjustments, Write-Offs and Voids",
                groups: ["AR / Patient Billing"],
                help: "When enabled, staff must choose a reason from the Statement Reason / Credit Codes list above when posting an adjustment or write-off, or voiding a payment, and the API rejects any other value. Add at least one code before enabling this — with the list empty, these actions are blocked until an administrator adds one.",
            },
            arStatementPaymentInstructions: {
                title: "Statement Payment Instructions",
                groups: ["AR / Patient Billing"],
                type: "textarea",
                help: "Free text shown on rendered statement PDFs explaining how and where patients can pay their balance.",
            },
            arStatementFooter: {
                title: "Statement Footer",
                groups: ["AR / Patient Billing"],
                type: "textarea",
                help: "Custom footer shown at the bottom of statement PDFs (e.g. remittance address, pharmacy contact line, disclaimers).",
            },
            refillTextDays: {
                group: "Refills",
                help: "How often to send refill reminder texts for newer prescriptions (written within the last 90 days). If set to 7, reminders are sent every 7 days (at 7, 14, 21, 28 days before the refill is due).",
            },
            oldScriptRefillTextDays: {
                group: "Refills",
                help: "How often to send refill reminder texts for older prescriptions (written more than 90 days ago). If set to 14, reminders are sent every 14 days (at 14, 28, 42 days before the refill is due). Defaults to 14 if left empty.",
            },
            licenseExpirationAlertDays: {
                title: "License Expiration Alert Days",
                help: "Number of days before patient license expiration to show alert",
                groups: ["Patients"],
                min: 0,
                max: 365,
                type: "number",
            },
            npcTextHours: {
                title: "NPC Text Hours",
                help: "Number of Hours Between texts",
                group: "Text Messaging",
                min: 0,
            },
            awaitingInsuranceHours: {
                title: "Awaiting Insurance Text Hours",
                help: "Number of Hours Between texts",
                group: "Text Messaging",
                min: 0,
            },
            outOfNetworkHours: {
                title: "Out of Network Text Hours",
                help: "Number of Hours Between texts",
                group: "Text Messaging",
                min: 0,
            },
            refillTextSendDelaySeconds: {
                title: "Refill Text Send Delay (Seconds)",
                help: "Delay in seconds between each refill notification text sent. Set to 0 for no delay. Max 180 (3 minutes).",
                group: "Text Messaging",
                min: 0,
                max: 180,
            },
            shipmentNotifySendDelaySeconds: {
                title: "Shipment Notify Send Delay (Seconds)",
                help: "Delay in seconds between each shipment tracking notification text sent. Set to 0 for no delay. Max 180 (3 minutes).",
                group: "Text Messaging",
                min: 0,
                max: 180,
            },
            deliveryConfirmationSendDelaySeconds: {
                title: "Delivery Confirmation Send Delay (Seconds)",
                help: "Delay in seconds between each delivery confirmation text sent. Set to 0 for no delay. Max 180 (3 minutes).",
                group: "Text Messaging",
                min: 0,
                max: 180,
            },
            bypassStoreHoursRefillTextWorker: {
                title: "Bypass Store Hours - Refill Text Worker",
                help: "When enabled, the Refill Text Worker will send texts regardless of the store's configured texting hours.",
                group: "Text Messaging",
            },
            bypassStoreHoursNPCTextWorker: {
                title: "Bypass Store Hours - NPC Text Worker",
                help: "When enabled, the NPC Text Worker will send texts regardless of the store's configured texting hours.",
                group: "Text Messaging",
            },
            sendCallUsTextWhenNoValidZip: {
                title: 'Send "Call Us" Text When No Valid Zip Code',
                help:
                    "When enabled, the NPC Text Worker sends the \"NPC No Address\" text instead of the standard new prescription text " +
                    "for patients whose zip code is missing or shorter than five characters. Those patients cannot sign in to the patient " +
                    "app, which authenticates on date of birth plus zip code, so the standard text's confirm link would not work for them. " +
                    "The follow-up new prescription email is also skipped, because its link relies on the same sign-in.",
                group: "Text Messaging",
            },
            bypassStoreHoursWelcomeTextWorker: {
                title: "Bypass Store Hours - Welcome Text Worker",
                help: "When enabled, the Welcome Text Worker will send texts regardless of the store's configured texting hours.",
                group: "Text Messaging",
            },
            bypassStoreHoursNoRefillsRemainingTextWorker: {
                title: "Bypass Store Hours - No Refills Remaining Text Worker",
                help: "When enabled, the No Refills Remaining Text Worker will send texts regardless of the store's configured texting hours.",
                group: "Text Messaging",
            },
            disableWelcomeTextForUnsortedEscripts: {
                title: "Disable Welcome Text for Unsorted eScripts",
                help: "When enabled, eScripts that have not yet been sorted to a program will not receive a welcome text. Sorted eScripts still respect their per-program welcome text settings.",
                group: "Text Messaging",
            },
            reversalQueueDays: {
                title: "Reversal Queue Days",
                help: "Number of days after fill date before a prescription becomes eligible for reversal. For example, setting this to 1 means prescriptions will be eligible for reversal 1 day after being filled, while 10 means 10 days after being filled.",
                group: "Billing",
                type: "number",
            },
            initialRefillDays: {
                title: "Refill Cadence",
                help: "Days to calculated end of Day Supply. Negative numbers are before the end of the day supply.",
                group: "Text Messaging",
                type: "number-array",
            },
            noRefillsRemainingCadenceDays: {
                title: "No Refills Remaining Cadence",
                help: "Days from the end of days-supply when an 'out of refills' text is sent. Negative numbers are before exhaustion, positive after. Example: -10, -3, 7 sends 10 and 3 days before, and 7 days after. Leave empty to turn the feature off.",
                group: "Text Messaging",
                type: "number-array",
            },
            deliveryConfirmationDays: {
                title: "Delivery Confirmation",
                help: "Number of Days Post Delivery. Negative numbers will be ignored.",
                group: "Text Messaging",
                type: "number-array",
            },
            ordersAllowPV1Prescriptions: {
                title: "Allow PV1 Prescriptions On Orders",
                help: "Allow Prescriptions that require PV1, but which have not yet been completed, to be put on orders.",
                group: "Orders",
            },
            enableDuplicateOpenOrderCheck: {
                title: "Warn on Duplicate Open Orders",
                help: "When enabled, creating an order for a patient who already has an open order prompts staff to create anyway or merge into the existing order. A banner also appears on the patient dashboard.",
                group: "Orders",
                type: "boolean",
            },
            enableInstantCaptureFallback: {
                title: "Retry Unsupported Cards as Instant Capture",
                help: "Some cards reject a separate authorization and capture. When enabled, an authorization declined because the card does not support that is retried once as an immediate charge. Note this collects the full authorized amount when the order is created rather than placing a hold until it ships; if the order ships for less, the difference is refunded automatically. Off by default.",
                group: "Orders",
                type: "boolean",
            },
            pv1OnlyPrescriptionsOnOrders: {
                title: "PV1 Only Prescriptions On Orders",
                help: "When enabled, the PV1 queue ONLY shows prescriptions that are part of an active order. Prescriptions without orders will not appear in the PV1 queue.",
                groups: ["Prescriptions", "Orders"],
                type: "boolean",
            },
            requirePV1OnAllRefills: {
                title: "Require PV1 On All Refills",
                help: "When enabled, refills (Rf > 0) will also appear in the PV1 queue for verification. By default, only original fills (Rf = 0) require PV1.",
                group: "Prescriptions",
                type: "boolean",
            },
            allowFutureDatedPV2: {
                title: "Allow PV2 On Future Dated Prescriptions",
                help: "When enabled, PV2 can be completed on a prescription whose fill date has not arrived yet. PV1 is always allowed on future-dated prescriptions — use the PV1 queue's \"Show Future Dated\" filter to work them ahead of the fill date. Off by default, so PV2 stays blocked until the fill date arrives.",
                group: "Prescriptions",
                type: "boolean",
            },
            promptGenericSubstitutionOnBrand: {
                title: "Prompt for Generic Substitution on Brand Drug",
                help: "When enabled, saving a new prescription for a brand-name GSDD drug interrupts the save with a modal that lists on-hand generic equivalents. Selecting a generic substitutes it in one click; keeping the brand requires the user to select a DAW code and provide pharmacist initials, which are persisted to PrescriptionDawOverrideLog for audit. Fires on new fills and new refills regardless of origin (Written, Phone, Custom, Fax, eScript, Transfer).",
                group: "Prescriptions",
                type: "boolean",
            },
            clinicallySignificantFields: {
                title: "Clinically Significant Fields",
                help: "Prescription fields that, when modified after PV1 verification, will automatically revoke PV1 and require re-verification.",
                group: "Prescriptions",
                type: "checkbox-array",
                options: [
                    { text: "Drug (PackageID)", value: "PackageID" },
                    { text: "Patient", value: "PatientID" },
                    { text: "Prescriber", value: "PrescriberID" },
                    { text: "Quantity", value: "Quantity" },
                    { text: "Dispensed", value: "Dispensed" },
                    { text: "Day Supply", value: "DaySupply" },
                    { text: "Directions (SIG)", value: "Directions" },
                    { text: "Fill Date", value: "FillDate" },
                    { text: "Written Date", value: "WrittenDate" },
                    { text: "Refills", value: "Refills" },
                ],
            },
            skipOrderVerificationAfterPV1: {
                title: "Skip Order Verification After PV1",
                help: "When enabled, skips the order summary page that normally appears after completing PV1 for prescriptions that are on orders.",
                groups: ["Prescriptions", "Orders"],
                type: "boolean",
            },
            requireExplicitVerificationPV1: {
                title: "Require Explicit Verification PV1",
                group: "Prescriptions",
                help: "When enabled, pharmacists must explicitly verify PV1 by clicking a verification button before prescriptions can proceed.",
            },
            validatePrescriberAuthority: {
                title: "Validate Prescriber State Authority",
                group: "Prescriptions",
                help: "When enabled, every new prescription is checked against the Universal provider directory at intake: NPI must be active, and the prescriber must hold an active state license matching the patient's address state. Controlled-substance prescriptions additionally require a valid DEA registration. Failures place the Rx on hold with the 'Invalid Prescriber Authority' sub-status for pharmacist review instead of rejecting it outright.",
            },
            requireExplicitVerificationPV2: {
                title: "Require Explicit Verification PV2",
                group: "Prescriptions",
                help: "When enabled, pharmacists must explicitly verify PV2 by clicking a verification button before prescriptions can be finalized.",
            },
            disablePV1NeededWarning: {
                title: "Disable PV1 Needed Warning On PV2",
                group: "Prescriptions",
                type: "boolean",
                help: "When enabled, suppresses the \"PV1 Needed\" reminder modal that appears on the PV2 screen for prescriptions where PV1 was not performed. Individual programs can additionally enable this suppression via the \"Disable PV1 Needed Warning\" flag on their Features tab; a program cannot re-enable the warning once it is disabled here. Useful for tenants who have moved away from the PV1 workflow (e.g., compounding-only operations).",
            },
            blockPV1OnPendingAllergyUpdate: {
                title: "Block PV1 When Patient Has Pending MAC Data",
                group: "Prescriptions",
                type: "boolean",
                help: "When enabled, PV1 cannot be completed for a patient who has unprocessed Medications, Allergies and Conditions (MAC) submissions waiting in the Patient Allergy and Meds Updates queue, so prescriptions are not verified against a profile that is known to be stale. Process the patient's queued items to clear the block. PV2 already blocks on pending MAC data regardless of this setting. Off by default.",
            },
            defaultIngestionQueueSelectAllForPatient: {
                title: "Default Rx Intake Queue to Select All for Patient",
                group: "Prescriptions",
                type: "boolean",
                help: "When enabled, selecting an item in the Rx Intake Queue also selects that patient's other items on the page, so a patient's scripts are processed together.",
            },
            showMoveToBucketOnProcessing: {
                title: "Show Move to Bucket on Processing Screen",
                group: "Prescriptions",
                type: "boolean",
                help: "When enabled, the prescription processing screen shows a \"Move to Bucket\" action so techs can reroute an Rx Intake Queue item to another bucket without leaving processing, then auto-advance to the next item. Requires the \"Processing/Rx Intake Queue/Move Item\" permission. Off by default.",
            },
            moveToBucketDestinationFilter: {
                title: "Move to Bucket Destination Options",
                group: "Prescriptions",
                type: "select",
                options: [
                    { value: "All", text: "All buckets" },
                    { value: "ProgramOnly", text: "Program buckets only" },
                    { value: "NonProgramOnly", text: "Non-program buckets only" },
                ],
                help: "Controls which buckets the processing screen's \"Move to Bucket\" action offers as destinations. Only applies when \"Show Move to Bucket on Processing Screen\" is enabled. Defaults to All buckets.",
            },
            useLastForDiffCheck: {
                title: "Use Last Verification for Difference Check",
                group: "Prescriptions",
                help: "When enabled, compares prescription changes against the most recent verification (PV1 or PV2) instead of always using PV1.",
            },
            hideDispensingFields: {
                title: "Hide Dispensing Fields",
                group: "Prescriptions",
                help: "Hides the lot number, drug expiration, serial number, and partner fill fields from prescription details.",
            },
            deaScheduleExpirations: {
                title: "DEA Schedule Expiration Defaults",
                help: "Default prescription expiration days for controlled substances by state and DEA class. The Default (DEF) row applies when a state has no specific rule.",
                group: "Prescriptions",
                type: "dea-schedule-expirations",
                fullWidth: true,
            },
            suppressEscriptDiagnosisAlert: {
                title: "Suppress Escript Diagnosis ICD10 Alert",
                group: "Prescriptions",
                help: "When enabled, hides the informational alert that lists ICD10 diagnosis codes received on an eScript on the Prescription Details page. Disabled by default — the alert continues to show.",
            },
            enforceEscriptEffectiveDate: {
                title: "Enforce eScript Effective Date",
                help: "When enabled, prescriptions parsed from inbound eScripts that carry an EffectiveDate cannot be filled before that date. Initial fills and refills auto-clamp the Fill Date to the Effective Date, and saving a Fill Date before the Effective Date is blocked unless the user has the Super Admin / Prescription permission.",
                group: "Prescriptions",
                type: "boolean",
            },
            autoRefillPromptStatement: {
                group: "Auto Refill",
                type: "textarea",
                help: "The automated prompt statement played to patients during the IVR (Interactive Voice Response) auto refill phone system in English. This message explains the auto refill program to patients.",
            },
            autoRefillPromptStatementSpanish: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Spanish.",
            },
            autoRefillPromptStatementRussian: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Russian.",
            },
            autoRefillPromptStatementSomali: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Somali.",
            },
            autoRefillPromptStatementArabic: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Arabic.",
            },
            autoRefillPromptStatementChineseSimplified: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Simplified Chinese.",
            },
            autoRefillPromptStatementVietnamese: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Vietnamese.",
            },
            autoRefillPromptStatementFarsi: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Farsi (Persian).",
            },
            autoRefillPromptStatementKorean: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Korean.",
            },
            autoRefillPromptStatementRomanian: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Romanian.",
            },
            autoRefillPromptStatementSwahili: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Swahili.",
            },
            autoRefillPromptStatementBurmese: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Burmese.",
            },
            autoRefillPromptStatementNepali: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Nepali.",
            },
            autoRefillPromptStatementAmharic: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Amharic.",
            },
            autoRefillPromptStatementPashto: {
                group: "Auto Refill",
                type: "textarea",
                help: "The prompt statement that is read out loud over the phone by pharmacy staff or read by the patient via the patient app in Pashto.",
            },
            autoCaseAppNewPatientCreation: {
                group: "Cases",
                help: "When enabled, automatically creates a case when a new patient account is created through the patient app, allowing staff to review and verify the new patient information.",
            },
            autoCaseTextsNoValidNumbers: {
                groups: ["Cases", "Text Messaging"],
                help: "When enabled, automatically creates a case if a patient has no valid phone numbers for text messaging. This helps identify and resolve patient contact information issues.",
            },
            autoCaseTextsPatientContactFailure: {
                groups: ["Cases", "Text Messaging"],
                help: "When enabled, automatically creates a case when attempts to contact a patient fail repeatedly. This helps ensure important communications are not missed.",
            },
            autoCaseTextsFailAutoRefill: {
                groups: ["Cases", "Text Messaging", "Auto Refill"],
                help: "Automatically creates a patient case if one doesn't already exist if texts fail to send for an auto refill.",
            },
            autoAssignResolvedCasesToComplianceTeam: {
                title: "Auto-Assign Resolved Cases to Compliance Team",
                group: "Cases",
                type: "boolean",
                help: "When enabled, automatically assigns resolved cases to the first team with Admin/Compliance permission. The assignment will replace any existing assignment.",
            },
            notifyCaseCreatorOnResolution: {
                title: "Notify Case Creator on Resolution",
                group: "Cases",
                type: "boolean",
                help: "When enabled, automatically sends a notification to the user who created a case when it is resolved, rejected, or accepted.",
            },
            enforceStoreStateLicensingForCasesConsultations: {
                title: "Enforce Store State Licensing for Cases & Consultations",
                groups: ["Cases", "Consultations"],
                type: "boolean",
                help: "When enabled, a case or consultation cannot be saved if its assigned store is not licensed to ship to the patient's state. Patients with no state on file are unaffected. The Cases and Consultations lists filter on store licensing regardless of this setting.",
            },
            partialPaymentTextThresholdAmount: {
                groups: ["Text Messaging"],
                help: "When amount owed is over this amount, text will refer patient to call instead of sending a web link if the order is put into partial payment status.",
                min: 0,
            },
            npcTextThresholdAmount: {
                groups: ["Text Messaging"],
                title: "NPC Text Threshold Amount",
                help: "When amount owed is over this amount, text will refer patient to call instead of sending a web link if it is a new prescription.",
                min: 0,
            },
            infinitrakApiKey: {
                title: "InfiniTRAK API Key",
                groups: ["Integrations"],
                help: "API key for integrating with the InfiniTRAK inventory management system. This enables automatic inventory synchronization and tracking.",
            },
            checkMyRxCostEnabled: {
                title: "CheckMyRxCost (BuyRx.ai) Integration",
                groups: ["Integrations", "Inventory"],
                type: "boolean",
                help: "When enabled, the BuyRx.ai purchase-options panel appears on the Drug Maintenance page and bulk add-to-cart is available on Inventory PAR Levels. Requires a CheckMyRxCost subscription key to be configured for this pharmacy.",
            },
            irsReportEmail: {
                hidden: true,
            },
            disableMedGuidePrinting: {
                title: "Disable MedGuide Printing",
                groups: ["Printing"],
                help: "When enabled, prevents automatic printing of medication guides with prescriptions. You can disable this if you are manually printing drug medication guides or do not require them.",
            },
            doNotPrintShippingTicket: {
                title: "Do Not Print Shipping Ticket",
                groups: ["Printing"],
                help: "When enabled, suppresses the shipping ticket (packing slip) at Order Verification for 1-step-shipping stores. The carrier shipping label still prints. Has no effect on 2-step shipping stores, will-call orders, or in-store delivery orders — those continue to print their ticket as usual.",
            },
            inventoryReasons: {
                type: "string-array",
                groups: ["Inventory"],
                addLabel: "Add Reason",
                maxlength: 40,
                reorderable: true,
                help: "Define custom reasons for inventory adjustments. These are used when adding or removing inventory to document why the adjustment was made, improving inventory tracking and auditing.",
            },
            inventoryLotStatuses: {
                type: "inventory-lot-statuses",
                title: "Inventory Lot Statuses",
                groups: ["Inventory"],
                help: "Define statuses that can be assigned to inventory lots. Each status can be marked as eligible or ineligible for filling. Lots with an ineligible status will be blocked from use during the filling process.",
            },
            textMessageTemplates: {
                type: "text-message-templates",
                title: "Text Messages",
                groups: ["Text Messaging"],
                fullWidth: true,
                help: "Create and manage custom SMS message templates for various pharmacy workflows. Templates support dynamic tokens that are replaced with patient and prescription information when messages are sent.",
            },
            emailTemplates: {
                type: "email-templates",
                title: "Email Templates",
                groups: ["Email"],
                fullWidth: true,
                help: "Create and manage custom email templates for patient communications. Templates support dynamic tokens and HTML formatting for professional, branded emails. Emails use SendGrid. Additional system requirements apply. Contact Scriptly support for details.",
            },
            readyForPickupTexts: {
                title: "Ready for Pickup Texts",
                groups: ["Text Messaging"],
                help: "When enabled, automatically sends text messages to patients when their prescriptions are ready for pickup at the pharmacy. This can be disabled at the Store configuration level.",
            },
            disableWebNotifications: {
                title: "Disable System Presented Notifications",
                groups: ["Notifications"],
                help: "Disables presenting notifications using native operating system notifications (ie, Windows notifications). Notifications will still appear within the application as a fallback.",
            },
            orderCancellationReasons: {
                type: "string-array",
                groups: ["Orders"],
                addLabel: "Add Cancellation Reason",
                maxlength: 40,
                reorderable: true,
                help: "Define custom reasons for why orders are cancelled. These help track cancellation patterns and improve order fulfillment processes.",
            },
            requireLotsOnInventoryBatches: {
                groups: ["Inventory"],
                help: "When enabled, requires lot numbers to be entered when processing inventory batches. This ensures proper tracking of medication lots for safety and recall purposes.",
            },
            requireExpirationOnReturnBatch: {
                title: "Require Expiration Date on Return Batch",
                groups: ["Inventory"],
                help: "When enabled, requires every lot on a Return Batch to include an expiration date. When disabled (default), Return Batches may be submitted with blank expiration dates — matching the underlying Inventory.ExpirationDate column, which is optional.",
            },
            blockDeactivatingDrugIfInventoryOnHand: {
                groups: ["Inventory"],
                help: "When enabled, prevents deactivating drugs that have inventory on hand. This helps avoid accidentally deactivating drugs that are still in stock and available for dispensing.",
            },
            inventoryBatchSubmitEndpoint: {
                groups: ["Inventory"],
                help: "Pushes inventory batch data including any lots and expiry dates to provided endpoint.",
            },
            autoFinishSubscription: {
                title: "Auto Finish Subscription",
                groups: ["Auto Refill"],
                type: "boolean",
                help: "When enabled, auto refill subscriptions are automatically cancelled when the prescription has no remaining quantity or has expired. When disabled, these subscriptions remain in the auto refill queue for manual review by staff.",
            },
            allowAutorefillWithoutNotification: {
                groups: ["Notifications", "Auto Refill"],
                help: "When enabled, allows auto refill orders to be processed even if patient notification (texts) fail or is not completed. Use with caution as patients may not be aware of upcoming refills.",
            },
            allowAutorefillWithoutPayment: {
                groups: ["Auto Refill"],
                help: "When enabled, allows auto refill orders to be processed even if payment has not been collected. This creates a balance due (partial payment) on the patient's order that must be collected before dispensing can occur.",
            },
            onHoldSubStatuses: {
                groups: ["Prescriptions"],
                type: "on-hold-substatus-field",
                fullWidth: true,
                lockedIds: [2, 6, 8, 13, 14],
                addLabel: "Add Sub Status",
                help: "Define custom sub-status reasons for prescriptions that are on hold. Configure an optional time offset (Hours/Days) per substatus to automatically calculate the Next Touch Date when a prescription is placed on hold with that substatus.",
            },
            requireNextTouchDateOnHold: {
                title: "Require Next Touch Date on On-Hold Prescriptions",
                groups: ["Prescriptions"],
                type: "boolean",
                help: "When enabled, requires a Next Touch Date to be set when saving a prescription with on-hold status. The date must be in the future. When disabled, the Next Touch Date field is available but optional.",
            },
            enablePrescriberNpiValidation: {
                title: "Enable Prescriber NPI Validation",
                groups: ["Prescriptions"],
                type: "boolean",
                help: "When enabled, prescriber NPIs are validated against the InfoWerks provider registry. Validation results are displayed on prescription details and during PV1/PV2 verification, showing whether the NPI is active, expired, inactive, or sanctioned.",
            },
            enableSupervisingPhysician: {
                title: "Enable Supervising Physician on Prescribers",
                groups: ["Prescriptions"],
                type: "boolean",
                help: "When enabled, the Doctor Record form exposes a <strong>Requires Supervising Physician</strong> checkbox on each prescriber. When that checkbox is on, the prescriber's Supervising Physician section (Name + Credentials) appears and both fields are required before save. When this tenant toggle is off, no supervising-physician UI is shown anywhere.",
            },
            requireDirectionsAtEntry: {
                title: "Require Directions (Sig) at Prescription Entry",
                groups: ["Prescriptions"],
                type: "boolean",
                help: "When enabled, the directions (Sig) field must be filled in before a prescription can be saved. The Save button stays disabled until directions are entered. When disabled, directions remain optional.",
            },
            enableRxLevelDiagnosis: {
                title: "Enable Diagnosis Codes at the Rx Level",
                groups: ["Prescriptions"],
                type: "boolean",
                help: "When enabled, the prescription form lets you select ICD10 diagnosis codes for the individual Rx, chosen from the patient's existing diagnoses or searched from the full ICD10 catalog. Any code selected that is not already on the patient is added to the patient record. Selecting a code is always optional — it is never required to save an Rx. When disabled, no Rx-level diagnosis UI appears.",
            },
            discontinuedSubStatuses: {
                groups: ["Prescriptions"],
                type: "name-active-field",
                lockedIds: [],
                addLabel: "Add Sub Status",
                reorderable: true,
                help: "Define custom sub-status reasons for discontinued prescriptions. These provide additional detail about why a prescription was discontinued and help with reporting and patient communication.",
            },
            includePrescriptionImageOnExternalTransfers: {
                title: "Allow Including Prescription Image on External Transfers",
                groups: ["Prescriptions"],
                type: "boolean",
                help: "When enabled, users can optionally provide the prescription image with external prescription transfers."
            },
            showCrossPatientHardCopyScripts: {
                title: "Show Cross-Patient Hard Copy Prescriptions",
                groups: ["Prescriptions"],
                type: "boolean",
                help: "When enabled, the \"prescriptions on this hard copy\" panel offers an option to expand and view prescriptions saved from the same scanned image that belong to a different patient (e.g. a multi-patient fax split via the PDF Chopper). When disabled (default), those cross-patient prescriptions are hidden behind an informational note.",
            },
            hideSamePatientHardCopyScripts: {
                title: "Hide Same-Patient Hard Copy Prescriptions",
                groups: ["Prescriptions"],
                type: "boolean",
                help: "When enabled, the \"prescriptions on this hard copy\" list under the prescription image is hidden on the prescription details and verification screens. Fill history remains available through the existing history views. The cross-patient note warning that prescriptions from the same image belong to a different patient is **not** affected and still appears.",
            },
            faxCoverPage: {
                groups: ["Faxing"],
                help: "When enabled, automatically includes a cover page with ALL outbound faxes. Cover page content requires setup by Scriptly support.",
            },
            faxRoutingRules: {
                groups: ["Faxing"],
                help: "Enables automatic program routing based on faxes you received to the numbers configured here. (The number must be on the Documo account configured for your instance.) Fax Numbers should start with +1 followed by the 10 digit number. (eg. +19999999999) Contact Scriptly support for assistance setting up fax routing rules.",
                type: "fax-routing-rule",
            },
            faxTemplateOverrides: {
                title: "Fax Templates",
                groups: ["Faxing"],
                type: "fax-templates",
                fullWidth: true,
                help: "Configure custom fax templates to override the default system templates. These overrides will be used across all stores and programs unless they have their own overrides.",
            },
            patientAppCheckoutAttestation: {
                groups: ["Orders"],
                type: "textarea",
                help: "The attestation text that appears on the checkout page in the patient app.<br><br><strong>Tokens:</strong><ul><li>{{AuthorizeBlock}}: will be replaced with 'I, Patient Name, authorize Pharmacy Name'</li><li>{{PharmacyName}}: will be replaced with the pharmacy name.</li></ul>",
            },
            enablePostDeliverySurvey: {
                title: "Enable Patient App Post-Delivery Survey",
                groups: ["Patient App"],
                help: "Kill switch. ON shows the survey, OFF hides it. Resolution order: <strong>Store → Program → Tenant</strong>, first match wins. Falls back to a seeded default that always stays in the DB.",
            },
            patientAppDeliverySurveyQuestionSetId: {
                title: "Patient App Post-Delivery Survey Question Set",
                type: "select",
                groups: ["Patient App"],
                help: "Tenant-default question set rendered to the patient after they confirm a delivery in the Patient App. Stores and Programs may override this. Use the 'Create / Manage' option below to add or edit available question sets.",
                options: this.patientAppDeliverySurveyQuestionSets.length === 0
                    ? [
                        { value: null, text: "(no question sets yet)" },
                        { value: "__manage__", text: "+ Create your first question set..." }
                      ]
                    : [
                        { value: null, text: "" },
                        ...this.patientAppDeliverySurveyQuestionSets.map((qs: any) => ({ value: qs.id, text: qs.name })),
                        { value: "__manage__", text: "+ Create / Manage question sets..." }
                      ],
            },
            blockC2PatientAppOrders: {
                title: "Block C2 Drugs from Patient App Ordering",
                type: "boolean",
                groups: ["Patient App", "Orders", "Auto Refill"],
                help: "When enabled, Schedule II controlled substances are excluded from the patient app order flow and from auto-refill signup eligibility. Patients will not see C2 prescriptions as available to order, and any direct API attempt to order one will be rejected. Use this to ensure pharmacy staff review concurrent opioid use before C2s are dispensed.",
            },
            enablePositiveIdentification: {
                title: "Require Positive Identification",
                type: "boolean",
                groups: ["Compliance"],
                help: "When enabled, regulated pharmacy actions (entry, verification, DUR, dispensing, compounding, annotations) require a second identification factor in addition to the logged-in session, and each is recorded to a tamper-evident audit log. Required in jurisdictions that mandate positive identification at each regulated pharmacy workflow point.",
            },
            positiveIdMethods: {
                title: "Positive Identification Methods",
                type: "checkbox-array",
                groups: ["Compliance"],
                help: "The secondary-factor methods staff may use to satisfy positive identification. Requires hardware enrollment per method.",
                options: [
                    { text: "Badge / proximity card", value: "badge" },
                    { text: "Fingerprint (biometric)", value: "biometric" },
                ],
            },
            positiveIdBiometricConsentDocumentId: {
                title: "Biometric Privacy Disclosure PDF",
                type: "biometric-consent-pdf",
                fullWidth: true,
                groups: ["Compliance"],
                help: "Upload the biometric privacy disclosure PDF each staff member must read and acknowledge before fingerprint enrollment. The uploaded file is referenced by every consent record, and a hash of its bytes is stored alongside — replacing the PDF automatically requires staff to re-consent against the new document. Have your legal counsel review this PDF before enabling biometric in production.",
            },
            patientRightsPdfImageId: {
                title: "Patient Rights PDF (Notice of Privacy Practices)",
                groups: ["Patient App"],
                type: "patient-rights-pdf",
                fullWidth: true,
                help: "Upload a custom PDF for the Notice of Privacy Practices (NPP) displayed in the patient app checkout. This is a tenant-level configuration that applies to all stores and programs. Only PDF files are accepted.",
            },
            allowPatientPickup: {
                title: "Allow Patient Pickup",
                groups: ["Patient App"],
                type: "boolean",
                help: "When enabled, patients placing an order in the patient portal can request pickup (will-call) at the pharmacy instead of delivery. Pickup is only offered when the dispensing store has 'Allow Patient Pickup' enabled; otherwise delivery (the default) is used. Patient choice is recorded on the order and exposed in reporting for fulfillment-mix tracking.",
            },
            enforceDriversLicenseUploadAtCheckout: {
                title: "Require Driver's License at Patient App Checkout",
                groups: ["Patient App", "Orders"],
                type: "boolean",
                help: "When enabled, a patient app self-checkout will not finalize unless the patient has a current, non-expired driver's license that has been reviewed and processed by pharmacy staff. An unreviewed upload, an expired license, or no license on file blocks checkout and prompts the patient to upload a current one.",
            },
            enableDiscountCardEntry: {
                title: "Enable Discount Card Entry at Checkout",
                groups: ["Patient App"],
                type: "boolean",
                help: "When enabled, the patient app lets a patient enter a discount card (BIN/PCN) at checkout, verify the discount price against the copay, and opt to bill the discount card instead of insurance. Ships off; requires configured Coupon plans and live claim adjudication.",
            },
            enablePatientAppDrugPriceSearch: {
                title: "Enable Patient App Drug Price Search",
                groups: ["Patient App"],
                type: "boolean",
                help: "When enabled, patients can search medications in the patient app and see the cash price for a chosen quantity, plus a copay vs. cash-price comparison for their current medications. Informational only — it does not change what the patient is charged. Requires a Cash Price Schedule. Unrelated to discount card entry at checkout.",
            },
            patientAppDrugPriceSearchScheduleId: {
                title: "Patient App Cash Price Schedule",
                groups: ["Patient App"],
                type: "select",
                options: [
                    { value: null, text: this.patientAppPriceSchedules.length === 0 ? "(no price schedules found)" : "" },
                    ...this.patientAppPriceSchedules.map((ps: any) => ({ value: ps.id, text: ps.name })),
                ],
                help: "Price Schedule used to quote cash prices for Drug Price Search. Leave blank to keep drug price search off even when the toggle is on. Unrelated to discount card entry at checkout.",
            },
            enablePatientAddressOrderGuidance: {
                title: "Patient App Address Guidance",
                groups: ["Patient App"],
                type: "boolean",
                help: "When enabled, the Patient App adds extra guidance around addresses: it clarifies which address orders ship to when a patient has more than one, and warns a patient who adds or edits an address while they have an order in progress to contact the pharmacy (the edit is not blocked). Default off; existing single-address and no-active-order flows are unaffected.",
            },
            enablePatientAddressValidationOnSave: {
                title: "Validate Patient Address on Save (Admin)",
                groups: ["Patients"],
                type: "boolean",
                help: "When enabled, the admin app prompts to accept a USPS-standardized suggestion before saving. A validation warning is shown on save either way — this toggle only controls the interactive prompt. Default off. Shipping always uses the saved validated address regardless of this setting.",
            },
            defaultPatientAddressType: {
                title: "Default Patient Address Type",
                groups: ["Patients"],
                type: "select",
                options: [
                    { value: null, text: "Mailing (default)" },
                    { value: PatientAddressType.shipping, text: "Shipping" },
                    { value: PatientAddressType.billing, text: "Billing" },
                ],
                help: "Address type pre-selected when staff create a patient or add an address to one. Staff can still change the type on each address. Shipping-type addresses are where automatic refills ship. Defaults to Mailing.",
            },
            enablePatientAddressValidationPatientApp: {
                title: "Validate Patient Address in Patient App",
                groups: ["Patient App"],
                type: "boolean",
                help: "When enabled, the Patient App checks a patient's address against USPS on save and prompts to accept a standardized suggestion. Default off.",
            },
            enablePatientAddressValidationOnDashboard: {
                title: "Validate Patient Address on Dashboard",
                groups: ["Patients"],
                type: "boolean",
                help: "When enabled, the patient dashboard shows an alert if the patient's primary address fails USPS validation. Default off.",
            },
            requireExplicitDur: {
                title: "Require Explicit DUR Acknowledgment",
                type: "select",
                options: [
                    {
                        value: null,
                        text: "Not Required",
                    },
                    {
                        value: 2,
                        text: "Major Severity Only",
                    },
                    {
                        value: 1,
                        text: "Moderate and Major Severity Only",
                    },
                    {
                        value: 0,
                        text: "All Severities",
                    },
                ],
                groups: ["Prescriptions"],
                help: "Require pharmacists to explicitly acknowledge Drug Utilization Review (DUR) alerts based on severity level. This ensures critical drug interactions and clinical alerts are not overlooked.",
            },
            requireDurNotes: {
                title: "Require DUR Notes on Explicit DUR",
                groups: ["Prescriptions"],
                help: "When enabled, pharmacists must enter notes explaining their decision when acknowledging DUR (Drug Utilization Review) alerts. This provides documentation for clinical decisions.",
            },
            enableRTPB: {
                groups: ["Prescriptions"],
                title: "Enable Real-Time Prescription Benefit (RTPB)",
                help: "When enabled, adds a 'Check Benefits' button to prescriptions for getting real-time benefits information. Contact Scriptly support for configuration and details.",
            },
            patientDocumentTags: {
                type: "string-array",
                groups: ["Patients"],
                maxlength: 40,
                reorderable: true,
                help: "Define custom tags for categorizing patient documents. Tags help organize and quickly find specific types of documents such as ID cards, insurance forms, prescriptions, or medical records.",
            },
            saveTransferDocumentToPatientDocuments: {
                title: "Transfer Document to Patient Documents",
                groups: ["Patients"],
                help: "Save transfer faxes to patient documents with a 'Transfer' tag automatically.",
            },
            requirePatientDocumentTags: {
                title: "Require Patient Document Tags",
                groups: ["Patients"],
                type: "boolean",
                help: "Require at least one tag when adding patient documents. (only when tags are available)",
            },
            requireReasonForPatientMacRemoval: {
                title: "Require Reason When Removing Medications, Allergies & Conditions",
                groups: ["Patients"],
                type: "boolean",
                help: "When enabled, staff must enter a reason before removing an allergy, condition, or non-profile medication from a patient's profile. Removed items are archived (rather than deleted) and can be viewed, along with the reason, in each section's archived view.",
            },
            requireEligibleProgramForPatient: {
                title: "Require Eligible Program for Patient",
                groups: ["Patients"],
                type: "boolean",
                help: "When enabled, patients cannot be created or updated in the Admin UI without at least one eligible program assigned. This does not affect API patient creation from external integrations.",
            },
            hideIncompleteConsultationsOnPrintQueue: {
                title: "Hide Patients with Incomplete Consultations on Print Queue",
                groups: ["Consultations"],
                help: "When enabled, patients with incomplete pharmacist consultations will not appear in the print queue until all consultation requirements are completed.",
            },
            hideIncompleteAnswersOnPrintQueue: {
                title: "Hide Prescriptions with Incomplete Answers on Print Queue",
                groups: ["Printing"],
                type: "boolean",
                help: "When enabled, prescriptions with incomplete clinical screening questions will not appear in the print queue until all required questions are answered. Configure questions in the Categories section.",
            },
            transferReasons: {
                groups: ["Prescriptions"],
                type: "name-active-field",
                addLabel: "Add Transfer Reason",
                reorderable: true,
                help: "Define custom reasons for why prescriptions are being transferred to or from your pharmacy. These help with tracking and reporting prescription transfer patterns.",
            },
            patientAssistanceProgramRejectReasons: {
                type: "string-array",
                groups: "Patient Assistance Program",
                maxlength: 40,
                reorderable: true,
                help: "Define custom reasons for why patient assistance program applications are rejected. These help track common rejection causes and improve future application success rates.",
            },
            papNextTouchDays: {
                title: "PAP Next Touch Days",
                groups: "Patient Assistance Program",
                fullWidth: false,
                type: "pap-next-touch",
                help: "Number of days before the next required touchpoint",
            },
            priorAuthStatusReasons: {
                title: "Prior Authorization Status Reasons",
                groups: ["Prior Authorization"],
                fullWidth: false,
                type: "prior-auth-status-reasons",
                reorderable: true,
                help: "Define custom status reasons for prior authorization requests at each stage of the process. These provide detailed context about the current state and help track approval patterns. The same list is offered to prescribers in the provider portal, where a reason is required to abandon, deny or cancel a prior authorization; leave a reason unscoped to offer it for every status.",
            },
            priorAuthNextTouchDays: {
                title: "Prior Authorization Next Touch Days",
                groups: ["Prior Authorization"],
                fullWidth: true,
                type: "prior-auth-next-touch",
                help: "Configure automated follow-up schedules for prior authorization requests based on their status. This ensures timely follow-ups and prevents authorizations from expiring.",
            },
            priorAuthNoteTemplates: {
                title: "Prior Authorization Note Templates",
                type: "string-array",
                groups: ["Prior Authorization"],
                maxlength: 200,
                reorderable: true,
                help: "Create quick-select note templates for common prior authorization scenarios. Templates speed up documentation and ensure consistent communication.",
            },
            priorAuthOutcomeReviewTaskTemplateId: {
                title: "Prior Authorization Outcome Review Task Template",
                type: "task-template",
                help: "If a template is selected, a review task is created for staff whenever a prescriber records a terminal PA outcome (Approved, Denied, or Canceled) in the provider portal. If left blank, no review task is created.",
                groups: ["Prior Authorization"],
            },
            allowAllDrugsForElectronicPriorAuthorization: {
                title: "Allow All Drugs for Electronic Prior Authorization",
                groups: ["Prior Authorization"],
                type: "boolean",
                help: "When enabled, removes the requirement that a drug's package ID be present in the configured electronic prior authorization (EPA) allowlist. All drugs will be permitted for EPA creation. When disabled (default), only drugs in the EPA allowlist may have an EPA created.",
            },
            doctorFollowUpNextTouchDays: {
                title: "Doctor Follow Up Next Touch Days",
                groups: ["Doctor Follow Up"],
                type: "doctor-follow-up-next-touch",
                help: "Configure automated follow-up schedules for contacting prescribers based on the status of follow-up items. This ensures more efficient use of the doctor follow-up queue. Leave empty to opt out — newly created follow-ups will appear immediately on the patient profile and queue.",
            },
            doctorFollowUpMaxDays: {
                title: "Doctor Follow Up Max Days",
                help: "The maximum number of days before the follow up item is considered expired, and abandoned.",
                groups: ["Doctor Follow Up"],
                type: "number",
                min: 0
            },
            doctorFollowUpMaxAttempts: {
                title: "Doctor Follow Up Max Attempts",
                help: "The maximum number of contact attempts before the follow up item is automatically closed. Leave empty (or 0) to disable.",
                groups: ["Doctor Follow Up"],
                type: "number",
                min: 0
            },
            planTypeCanBeUnknown: {
                title: "Plan Type Can Be Unknown",
                groups: ["Insurance"],
                help: "When enabled, allows the plan type to be unknown when creating a new plan.",
            },
            caseProblems: {
                title: "Case Problems",
                groups: ["Cases"],
                type: "case-problems",
                fullWidth: true,
                help: "Define custom problem types that can be assigned to patient cases, along with default watchers who will be automatically notified when a case is created with that problem. These help categorize and track the nature of issues that need to be resolved, improving case management and reporting.",
            },
            caseCategories: {
                title: "Case Categories",
                groups: ["Cases"],
                type: "case-categories",
                help: "Define case categories for organizing and controlling access to cases. Drag to reorder. Categories can have permissions that restrict which teams and users can see cases in that category.",
                reorderable: true,
            },
            defaultCaseNextTouchDays: {
                title: "Default Case Next Touch Days",
                groups: ["Cases"],
                type: "number",
                min: 0,
                help: "Default number of days from the case's last update date to calculate the next touch date. This is used when a case problem type does not have a specific next touch days configured. Defaults to 2 days if not set.",
            },
            pharmacistOnlyNotes: {
                groups: ["Notes"],
                help: "When enabled, creates a special note type that only pharmacists can view. This is useful for clinical observations or sensitive information that should not be visible to all staff.",
            },
            excludeRefillRequestsFromNotes: {
                title: "Exclude refill requests from notes",
                groups: ["Notes"],
                type: 'boolean',
                help: "When enabled, refill request entries will not appear in the patient notes timeline. This reduces clutter in patient notes by hiding routine refill activities.",
            },
            enableInternalOnlyNotes: {
                title: "Enable Internal-Only Notes",
                groups: ["Notes"],
                type: 'boolean',
                help: "When enabled, the note form shows a visibility selector (Everyone, Pharmacist Only, Internal Only) and new notes default to Internal Only. Internal-only notes are visible to all internal staff but are hidden from the patient, provider, and external portals. When disabled, the note form keeps the existing Pharmacist Only switch and behavior is unchanged.",
            },
            enableVoicemailNoteActivity: {
                title: "Enable Voicemail Note Activity",
                groups: ["Notes"],
                type: 'boolean',
                help: "When enabled, the note form offers \"Add Voicemail\" alongside the other outreach activities, for recording that a message was left rather than that the patient was reached.",
            },
            enableAttemptedPhoneCallNoteActivity: {
                title: "Enable Attempted Phone Call Note Activity",
                groups: ["Notes"],
                type: 'boolean',
                help: "When enabled, the note form offers \"Add Phone Call - Attempted\" for a call that did not connect, so it can be told apart from a completed call. Attempted calls count toward a task's call attempts in reporting either way.",
            },
            createTaskOnMPPPClaimFromTaskTemplateId: {
                title: "Create Task from Template on MPPP Claim",
                groups: ["Tasks"],
                type: "task-template",
                help: "Automatically create a task when an MPPP (Medicare Prescription Payment Plan) claim is received. Select a task template to define the task details and assignment.",
            },
            createTaskOnMPPPEligibilityFromTaskTemplateId: {
                title: "Create Task from Template on MPPP Eligibility",
                groups: ["Tasks"],
                type: "task-template",
                help: "Automatically create a task when MPPP (Medicare Prescription Payment Plan) eligibility is determined. Select a task template to define the task details and assignment.",
            },
            providerFollowUpResponseTeamId: {
                title: "Provider Portal Follow Up Response Team",
                groups: ["Tasks"],
                type: "team",
                help: "Select the team that will be assigned tasks when providers submit follow-up responses through the provider portal. This ensures responses are routed to the appropriate staff.",
            },
            voicemailNotificationTeamId: {
                title: "Voicemail Notification Team",
                groups: ["Notifications"],
                type: "team",
                help: "Team that will receive notifications when new voicemails are created"
            },
            autoSendCancelRxResponse: {
                title: "Automatically Respond to CancelRx",
                groups: ["Prescriptions"],
                type: "boolean",
                help: "When a prescriber cancels an eScript that has not been entered as a prescription yet, automatically send an approved CancelRxResponse back to them. The eScript is dropped from the incoming queue either way, so the prescription is never filled; this only controls whether the prescriber is told the cancellation was accepted without anyone reviewing it first. When disabled, no response is sent. Cancellations for prescriptions that have already been entered always raise a task for staff and are unaffected by this setting.",
            },
            allowMultipleTaskAssignments: {
                title: "Allow Multiple Task Assignments",
                groups: ["Tasks"],
                type: "boolean",
                help: "When enabled, allows tasks to be assigned to multiple users or teams simultaneously. When disabled, tasks can only be assigned to a single user or team at a time.",
            },
            requireTaskAssignment: {
                title: "Require Task Assignment",
                groups: ["Tasks"],
                type: "boolean",
                help: "When enabled, all tasks must have at least one assignment before they can be saved. Templates are exempt from this requirement.",
            },
            requireTaskCancellationReason: {
                title: "Require Reason When Cancelling Tasks",
                groups: ["Tasks"],
                type: "boolean",
                help: "When enabled, the admin Cancel Task action requires a non-empty reason to be entered. Individual task templates can override this requirement via their own \"Require Cancellation Reason\" setting (a template-level value takes precedence over this global default).",
            },
            disableAllAI: {
                title: "Disable All AI",
                groups: ["AI"],
                type: "boolean",
                help: "Master switch to disable all AI-powered features throughout the system. When enabled, all AI functionality including insurance parsing, transcription, and other AI services will be turned off.",
            },
            disableInsuranceParsingAI: {
                title: "Disable Insurance Parsing AI",
                groups: ["AI", "Insurance"],
                type: "boolean",
                help: "Disables AI-powered parsing of insurance card images. When enabled, uploaded insurance card images will not be automatically processed to extract insurance information.",
            },
            enforceInsuranceAllowed: {
                title: "Enforce Insurance Allowed",
                groups: ["Insurance"],
                type: "boolean",
                help: "When enabled, the 'Insurance Allowed' flag is enforced on both Programs and Stores. If Insurance Allowed is unchecked on either the Program or the Store, insurance billing UI is hidden and insurance billing API endpoints return an error. Cash-out workflows are unaffected.",
            },
            enableAutoAdjudication: {
                title: "Enable Auto-Adjudication",
                groups: ["Insurance"],
                type: "boolean",
                help: "When enabled, technicians can stage a sequence of claims against a prescription ahead of its fill date, and a background worker bills them automatically once that date arrives. Fills whose outcome does not match what was staged are routed to the Billing Review queue instead of blocking the order. Also reveals the Schedule Claim action on the prescription and the Billing Review queue in the navigation. Off by default.",
            },
            enforceApprovedDrugList: {
                title: "Enforce Approved Drug List",
                groups: ["Insurance"],
                type: "boolean",
                help: "When enabled, claims are blocked for any plan that has opted in (via the plan's 'Enforce Approved Drug List' setting) unless the billed drug is on that plan's approved drug list within its effective/expiration window. Plans without the opt-in, and cash-out workflows, are unaffected.",
            },
            enforceBlockedDrugList: {
                title: "Enforce Blocked Drug List",
                groups: ["Insurance"],
                type: "boolean",
                help: "When enabled, claims are blocked for any plan that has opted in (via the plan's 'Enforce Blocked Drug List' setting) when the billed drug is on that plan's blocked drug list within its effective/expiration window. Plans without the opt-in, and cash-out workflows, are unaffected.",
            },
            hideEligibilityAndRtpbWhenInsuranceNotAllowed: {
                title: "Hide Eligibility & RTPB When Insurance Not Allowed",
                groups: ["Insurance"],
                type: "boolean",
                help: "When enabled and insurance is not allowed (per 'Enforce Insurance Allowed'), the Eligibility and RTPB buttons are hidden from the UI.",
            },
            disableFutureWrittenDates: {
                title: "Disable Future Written Dates",
                help: "Prevents setting prescription written dates to future dates. Super admins can always use future dates.",
                groups: ["Prescriptions"],
                type: "boolean"
            },
            enableEquivalentDrugCheck: {
                title: "Enable Equivalent Prescriptions Check on New Prescriptions",
                help: "When enabled, shows a prompt to discontinue equivalent prescriptions when saving new prescriptions. This check identifies other prescriptions for the same patient with equivalent drugs and offers to discontinue them. Disabling this improves save performance but may result in duplicate therapy going unnoticed.",
                groups: ["Prescriptions"],
                type: "boolean"
            },
            parseMiddleNameFromEscript: {
                title: "Parse Middle Name from E-Script",
                help: "When enabled, attempts to extract and store the middle name from e-script patient names.",
                groups: ["Prescriptions"],
                type: "boolean"
            },
            ...autoRefillCadenceFields(this.configuration.autoRefillNextProcessDateDefaultCadenceType),
            autoRemoveExpiredMedSyncPrescriptions: {
                title: "Automatically Remove Expired Prescriptions from MedSync",
                groups: ["Auto Refill"],
                help: "When enabled, a nightly job removes prescriptions from active MedSync enrollments once they pass their expiration date, and cancels the autorefill subscription aligned to each one. An enrollment left with fewer than two prescriptions is cancelled, since there is nothing left to synchronize. Every removal is recorded as a note on the enrollment. When disabled, expired prescriptions stay in the enrollment until someone removes them from the MedSync details screen.",
            },
            autoRefillCopayDifferenceAllowance: {
                title: "Copay Difference Allowance",
                help: "The amount a copay can increase by before preventing autorefill from being processed.",
                groups: ["Auto Refill"],
                type: "number",
                min: 0,
            },
            autoRefillAutoProcessAuditChance: {
                title: "Chance of Audit",
                help: "The chance an autoprocessed autorefill order goes into audit. 100% = always audit, 0% = never audit.",
                groups: ["Auto Refill"],
                type: "number",
                min: 0,
                max: 100,
            },
            dispenseErrorLabel: {
                title: "Error Label",
                groups: ["Prescriptions"],
                type: "string",
                help: "Rename the 'Dispense Error' page to a custom label that better fits your pharmacy's workflow.",
            },
            showTechnicianErrorEventStats: {
                title: "Count Errors Per Event in Technician Productivity Stats",
                groups: ["Prescriptions"],
                help: "When enabled, the technician productivity widget in the top navigation bar counts each dispense error individually instead of counting a prescription with any error once. A technician who typed 5 prescriptions where one carries 2 errors sees 2 errors and 3 scripts without errors, rather than 1 and 4. The technician productivity dashboard counts errors the same way, except that it also reports users who logged errors without typing any prescriptions in the period. The productivity score is unaffected: it still deducts once per prescription carrying errors, so a technician can see more errors listed than their score was docked for.",
            },
            prescriberFollowUpNoteTemplates: {
                title: "Doctor Follow Up Note Templates",
                type: "string-array",
                groups: ["Doctor Follow Up"],
                maxlength: 200,
                help: "Create quick-select note templates for common prescriber communication scenarios. Templates speed up documentation when following up with healthcare providers.",
            },
            specialities: {
                title: "Doctor Specialities",
                type: "specialities",
                groups: ["Doctor"],
                help: "Manage the list of medical specialties used for categorizing prescribers.",
            },
            accountManagerMode: {
                title: "Account Manager Mode",
                type: "select",
                groups: ["Doctor"],
                options: [
                    {
                        value: 0,
                        text: "Disabled",
                    },
                    {
                        value: 1,
                        text: "Single Prescriber",
                    },
                    {
                        value: 2,
                        text: "All Prescribers with NPI",
                    },
                ],
                help: "Enable assigning account managers (salespeople) to prescribers for reporting purposes. 'Single Prescriber' applies the account manager only to the specific prescriber. 'All Prescribers with NPI' applies the account manager to all prescribers sharing the same NPI number.",
            },
            allowShippingControlledWithoutPdmp: {
                title: "Allow Shipping Controlled Without PDMP Reporting",
                groups: ["Prescriptions"],
                keywords: ["pmp"],
                help: "When enabled, allows controlled substance prescriptions to be shipped even if PDMP (Prescription Drug Monitoring Program) reporting has not enabled for a state. Use with caution as this may affect regulatory compliance.",
            },
            pmpFailureTaskTeamId: {
                title: "PDMP Failure Task Team",
                groups: ["Prescriptions"],
                type: "team",
                keywords: ["pmp"],
                help: "Select the team that will be assigned tasks when PDMP (Prescription Drug Monitoring Program) submissions fail. This routes SFTP connection, authentication, and delivery failure alerts to the appropriate staff.",
            },
            enableShippingQualityCheck: {
                title: "Enable Shipping Quality Check",
                groups: ["Printing"],
                help: "When enabled, requires staff to scan each prescription barcode after scanning the order barcode on the Shipping page before printing labels. This enforces a verification step to ensure all prescriptions are physically present.",
            },
            enableBoxBarcodes: {
                title: "Enable Box Barcodes",
                groups: ["Shipping"],
                help: "When enabled, the Shipping page shows the Box Barcodes button and package dimension fields, and box barcodes can be scanned to set L×W×H used for rate quotes. Disabled by default.",
            },
            defaultShippingWeightPounds: {
                title: "Default Shipping Weight",
                groups: ["Shipping"],
                type: "number",
                min: 0.01,
                max: 150,
                step: 0.01,
                append: "lbs",
                keywords: ["lbs"],
                help: "Package weight used for one-step shipping labels, where the label prints at Order Verification and no scale weight is captured. Entered in **pounds**, not ounces — 0.75 = 12 oz, 1 = 16 oz. Leave blank to keep the previous behavior of 1 lb. Two-step shipping stores are unaffected; those labels still use the weight entered on the Shipping page.",
            },
            warnOnDuplicateShipment: {
                title: "Warn on Duplicate Shipment",
                groups: ["Printing"],
                help: "When enabled, if any prescription in the order matches a prescription (by store + Rx + refill) that has already been shipped in another order, the Shipping page shows a confirmation dialog before printing labels. Staff must acknowledge it to proceed.",
            },
            autoOutboundCadence: {
                title: "Auto Outbound Cadence",
                help: "Number of seconds between each auto outbound call attempt. Lower values means more calls going out at the same time. Consider your staffing schedules when adjusting this value.",
                type: "number",
                groups: ["Auto Outbound"],
                fullWidth: false,
            },
            autoOutboundStatusConfigurations: {
                title: "Auto Outbound Status Configurations",
                type: "auto-outbound-status-configuration",
                groups: ["Auto Outbound"],
                fullWidth: true,
                help: "Configure automated outbound calling behavior for different prescription statuses. This defines when and how the system should automatically call patients based on their prescription status.",
            },
            autoOutboundPartialPaymentConfiguration: {
                title: "Auto Outbound Partial Payment Configuration",
                type: "auto-outbound-configuration",
                groups: ["Auto Outbound"],
                fullWidth: true,
                help: "Configure automated outbound calling settings for patients with partial payment balances. This includes timing, messaging, and retry logic for payment collection calls.",
            },
            autoOutboundDeliveryConfirmationConfiguration: {
                title: "Auto Outbound Delivery Confirmation Configuration",
                type: "auto-outbound-configuration",
                groups: ["Auto Outbound"],
                fullWidth: true,
                help: "Configure automated outbound calling settings for delivery confirmations. This includes timing and messaging for calls confirming prescription deliveries with patients.",
            },
            rxTransferDeniedSubStatusId: {
                title: "Rx Transfer Denied Sub Status",
                type: "select",
                options: [
                    {
                        value: null,
                        text: "",
                    },
                    ...this.configuration.onHoldSubStatuses.map((i: any) => ({ value: i.id, text: i.name }))
                ],
                groups: ["Transfer"],
                help: "Select the on-hold sub-status to automatically apply when an electronic prescription transfer is denied by the external pharmacy. This helps track denied transfers and follow up appropriately via the on-hold queue page.",
            },
            rxTransferDeniedTaskTemplateId: {
                title: "Rx Transfer Denied Task Template",
                type: "select",
                help: "If a template is selected, a new task will be created based on the template when an electronic transfer is denied by the external pharmacy.",
                options: [
                    {
                        value: null,
                        text: "",
                    },
                    ...this.taskTemplates.map(template => ({ value: template.id, text: template.title }))
                ],
                groups: ["Transfer"]
            },
            requirePharmacistOnExternalTransfers: {
                title: "Require Pharmacist on External Transfers",
                groups: ["Transfer"],
                type: "boolean",
                help: "When enabled, requires a pharmacist to be selected for all external transfers. When disabled, pharmacist is optional for pharmacies that support electronic RxTransfer."
            },
            requireTransferReasonOnOutboundTransfers: {
                title: "Require Transfer Reason on Outbound Transfers",
                groups: ["Transfer"],
                type: "boolean",
                help: "When enabled, requires the Transfer Reason field to be completed for all outbound prescription transfers (both the RxTransfer queue and external/bulk transfers). Captures a reason on every outbound transfer for compliance and reporting."
            },
            disableSurescriptsETransfer: {
                title: "Disable SureScripts eTransfer",
                groups: ["Transfer"],
                type: "boolean",
                help: "Disables electronic prescription transfers via SureScripts."
            },
            enableAcceptAndTransfer: {
                title: "Enable Accept and Transfer",
                groups: ["Transfer"],
                type: "boolean",
                help: "When enabled, adds an 'Accept and Transfer' button to the PV1 verification page. This allows pharmacists to verify a prescription and transfer it to another pharmacy in a single workflow, automatically reversing any active claims."
            },
            warnOnDuplicateFaxForStoredPharmacy: {
                title: "Warn on Duplicate Fax for Stored Pharmacies",
                groups: ["Transfer"],
                type: "boolean",
                help: "When enabled, saving a stored pharmacy that shares a fax number with another pharmacy shows a confirmation prompt instead of blocking the save. Useful for chains where multiple locations share one fax line. Disabled by default."
            },
            providerPortalAccountCreationTaskTemplateId: {
                title: "Provider Portal Account Creation Task Template",
                type: "select",
                help: "If a template is selected, a new task will be created based on the template when a provider requests an account.",
                options: [
                    {
                        value: null,
                        text: "",
                    },
                    ...this.taskTemplates.map(template => ({ value: template.id, text: template.title }))
                ],
                groups: ["Provider Portal"]
            },
            entityAlertConfigurations: {
                title: "Entity Alert Configurations",
                type: "entity-alert-configuration",
                groups: ["Alerts"],
                fullWidth: true,
                help: "Configure individual alert settings including colors, display styles, and enable/disable functionality."
            },
            showAlertsOnFillingPage: {
                title: "Show Alerts on Filling Page",
                type: "boolean",
                groups: ["Alerts"],
                help: "When enabled, patient entity alerts will be displayed on the filling page."
            },
            requireCompoundNdcVerification: {
                title: "Require Compound NDC Verification",
                type: "boolean",
                groups: ["Prescriptions"],
                help: "When enabled, compound prescriptions must have their NDC barcode scanned and verified before labels can be printed."
            },
            requireCompoundLotNumber: {
                title: "Require Compound Lot Number",
                type: "boolean",
                groups: ["Prescriptions"],
                help: "When enabled, compound prescriptions must have a lot number entered before labels can be printed."
            },
            requireGtinVerification: {
                title: "Require GTIN Verification",
                type: "boolean",
                groups: ["Prescriptions"],
                help: "When enabled (default), a scanned UPC/NDC/GS1 barcode on the filling page must match the drug's GSDD-registered GTIN before a label can be printed. Disabling skips this GSDD GTIN match step so a mismatch no longer blocks printing (the scanned GTIN is still recorded for traceability). Compound NDC verification is governed separately by Require Compound NDC Verification and is unaffected."
            },
            enforcePackageSizeBilling: {
                title: "Enforce Package Size Billing",
                type: "boolean",
                groups: ["Prescriptions"],
                help: "When enabled, dispensed quantities must be even multiples of the drug's package size. Drugs measured in 'each' (ea) are exempt.",
            },
            patientDashboardConfiguration: {
                title: "Dashboard Card Configuration",
                groups: ["Patients"],
                type: "dashboard-configuration",
                help: "Configure the default card order, visibility, and cards hidden for all users. Users with the 'Customize Dashboard' permission can override the default layout, but hidden cards will always be hidden for all users.",
            },
            forbiddenPhoneNumbers: {
                title: "Forbidden Phone Numbers",
                type: "forbidden-phone-numbers",
                groups: ["Patients", "Doctor"],
                help: "Phone numbers that should not be used on patient or doctor profiles (e.g., internal pharmacy numbers). When saving a record with a forbidden number, the record will be saved but the forbidden number will be removed and a warning will be shown. An optional reason can be provided to explain why the number is forbidden.",
            },
            quickDialNumbers: {
                title: "Quick Dial Numbers",
                type: "quick-dial-numbers",
                groups: ["Twilio Flex"],
                fullWidth: true,
                help: "External numbers agents can select when transferring a call outbound in the Twilio Flex directory.",
            },
            requirePharmacistCompoundBatchVerification: {
                title: "Require Pharmacist Compound Batch Verification",
                type: "boolean",
                groups: ["Prescriptions"],
                help: "When enabled, compound batches require pharmacist verification before inventory is posted. Batches will enter an 'Awaiting Verification' status after completion."
            },
            enableActivityFactorAdjustment: {
                title: "Enable Compound Activity Factor Adjustment",
                type: "boolean",
                groups: ["Prescriptions"],
                keywords: ["compound", "compounding", "COA", "certificate of analysis", "assay", "potency", "LOD"],
                help: "Master toggle for potency-adjusted compound weigh-outs. When enabled, inventory lots and compound ingredients expose Certificate of Analysis fields (assay %, assay basis, loss on drying %) and ingredients flagged for adjustment have their weigh-out quantity corrected for the selected lot's potency, with the batch record capturing the factor applied. When disabled, none of the COA fields appear and compound filling behaves exactly as it did before. Turning it on for the first time seeds the acceptable ranges below with their defaults if all four are blank.",
            },
            activityFactorAssayMinPercent: {
                // Clamped above zero rather than at it: this bound also widens the hard plausibility
                // floor the weigh-out divides by, and 0 is the value that opens a divide-by-zero.
                // numberChange only clamps a typed number, so blank is still blank.
                title: "Acceptable Assay Minimum",
                type: "number",
                min: 1,
                step: "any",
                append: "%",
                groups: ["Prescriptions"],
                keywords: ["compound", "COA", "activity factor", "potency"],
                help: "Lowest Certificate of Analysis assay percentage accepted without a warning, inclusive. An assay below this raises a warning when the lot is entered and again at fill time; it never blocks. Default 90. Leave blank for no lower-bound warning.",
            },
            activityFactorAssayMaxPercent: {
                title: "Acceptable Assay Maximum",
                type: "number",
                min: 0,
                step: "any",
                append: "%",
                groups: ["Prescriptions"],
                keywords: ["compound", "COA", "activity factor", "potency"],
                help: "Highest Certificate of Analysis assay percentage accepted without a warning, inclusive. An assay above this raises a warning when the lot is entered and again at fill time; it never blocks. Default 110. Leave blank for no upper-bound warning.",
            },
            activityFactorLodMinPercent: {
                title: "Acceptable LOD Minimum",
                type: "number",
                min: 0,
                max: 100,
                step: "any",
                append: "%",
                groups: ["Prescriptions"],
                keywords: ["compound", "COA", "activity factor", "loss on drying", "water content"],
                help: "Lowest Certificate of Analysis loss on drying (water content) percentage accepted without a warning, inclusive. Only checked on lots whose assay basis is anhydrous or dried. Default 0. Leave blank for no lower-bound warning.",
            },
            activityFactorLodMaxPercent: {
                title: "Acceptable LOD Maximum",
                type: "number",
                min: 0,
                max: 100,
                step: "any",
                append: "%",
                groups: ["Prescriptions"],
                keywords: ["compound", "COA", "activity factor", "loss on drying", "water content"],
                help: "Highest Certificate of Analysis loss on drying (water content) percentage accepted without a warning, inclusive. Only checked on lots whose assay basis is anhydrous or dried. Default 10. Leave blank for no upper-bound warning.",
            },
            activityFactorDecimalPlaces: {
                title: "Compound Weigh-Out Decimal Places",
                type: "number",
                min: 0,
                max: 8,
                groups: ["Prescriptions"],
                keywords: ["compound", "activity factor", "precision", "rounding"],
                help: "Number of decimal places used to display activity-factor-adjusted weigh-out quantities on the compound fill page and batch record. Display only: quantities are calculated, stored and deducted at full precision regardless. Default 4.",
            },
            textShortcodes: {
                title: "Text Shortcodes",
                group: "Notes",
                type: "text-shortcodes",
                fullWidth: true,
                help: "Configure shortcodes that can be inserted into text fields by typing `/` followed by the code.",
            },
            allowAnnotationRevert: {
                title: "Allow Annotation Revert",
                type: "boolean",
                groups: ["Prescriptions"],
                help: "When enabled, displays a 'Revert to Original' button on prescription details allowing users to restore the original un-annotated hardcopy image.",
            },
            allowedCarriers: {
                title: "Allowed Carriers",
                type: "checkbox-array",
                groups: ["Shipping"],
                defaultAllChecked: true,
                options: [
                    { value: "FedEx", text: "FedEx" },
                    { value: "USPS", text: "USPS" },
                    { value: "UPS", text: "UPS" },
                    { value: "Ameriship", text: "Ameriship" },
                ],
                help: "Carriers this tenant permits. All carriers are permitted by default; uncheck to forbid one. Narrowed further by per-program and per-patient carrier allow-lists; the actual eligible set is the intersection of all three.",
            },
            allowedServiceTypes: {
                title: "Allowed Carrier Service Types",
                type: "carrier-service-checkbox",
                groups: ["Shipping"],
                serviceGroups: ServiceTypeGroups,
                help: "Carrier service types this tenant permits when creating labels. All services are permitted by default; uncheck a box to forbid that service. Switch tabs to filter by carrier. New tabs appear automatically as USPS/UPS catalogs are populated.",
            },
            allowedServiceAddOns: {
                title: "Allowed Carrier Service Add-ons",
                type: "carrier-service-checkbox",
                groups: ["Shipping"],
                serviceGroups: ServiceAddOnGroups,
                help: "Carrier service add-ons this tenant permits (e.g., signature requirements, Saturday delivery, dry ice). All add-ons are permitted by default; uncheck to forbid. Persisted now; per-shipment selection UI is delivered separately.",
            },
            enableUspsDirectShipping: {
                title: "Enable USPS Direct Shipping",
                type: "boolean",
                groups: ["Shipping"],
                help: "When enabled, USPS labels for stores with an active USPS Direct registration (CRID/MID/EPS payment account on file) are purchased directly from USPS instead of through ShipEngine. Stores without a registration are unaffected. Turning this off instantly reverts new purchases to ShipEngine. Persisted now; the rate-shop, tracking, void, and reprint behavior is delivered in the following release slices — until those land, flipping this on has no effect.",
            },
            enablePrescriberPortalLiveChat: {
                title: "Enable Prescriber Portal Live Chat",
                groups: ["Prescriber Portal"],
                type: "boolean",
                help: "When enabled, prescribers in the portal can start a live chat with an available Hub team member instead of calling in. Chats are answered in Twilio Flex and the full transcript is kept in the prescriber's communication history alongside regular messages. If no Hub agent is available, the prescriber is told so and their message is saved as a normal provider communication, which raises the usual notification. Turning this off hides the chat entry point and is also the kill switch if Twilio routing misbehaves. Requires the portal-chat Twilio resources to be provisioned; until they are, leave this off.",
            },
            prescriberPortalPasswordExpirationDays: {
                title: "Prescriber Portal Password Expiration Days",
                group: "Prescriber Portal",
                type: "number",
                min: 0,
                max: 730,
                append: "days",
                help: "Days a prescriber-portal user's Cognito password remains valid before they are blocked from logging in and forced to reset it. A non-dismissible banner warns the user during the final 14 days of the window. Set to 0 to disable expiration entirely. Default 180.",
            },
            enablePrescriberPortalNotifications: {
                title: "Enable Prescriber Portal Notifications",
                group: "Prescriber Portal",
                type: "boolean",
                help: "When enabled, prescribers are proactively notified by fax or email when a task is assigned to them in the portal, are sent one escalation notice if the task goes uncompleted, and see portal alerts for prescriptions placed on hold. The delivery channel follows each prescriber's Communication Preference. When disabled, nothing is sent and no alerts appear.",
            },
            prescriberPortalTaskEscalationDays: {
                title: "Prescriber Portal Task Escalation Days",
                group: "Prescriber Portal",
                type: "number",
                min: 0,
                max: 90,
                append: "days",
                help: "Days after the original notification before a single escalation notice is sent for a portal task that is still open. Only one escalation is ever sent per task. Set to 0 to disable escalation. Default 3.",
            },
            prescriberPortalAlertSubStatuses: {
                title: "Prescriber Portal Alert On-Hold Sub-Statuses",
                group: "Prescriber Portal",
                type: "string-array",
                addLabel: "Add Sub-Status",
                maxlength: 64,
                reorderable: true,
                help: "On-hold sub-status names that raise an alert in the prescriber portal, entered exactly as they appear in the On Hold Sub-Status list. A prescription shows an alert while it is on hold with one of these sub-statuses, and the alert clears as soon as it leaves On Hold. Leave empty to raise no on-hold alerts.",
            },
            prescriberPortalAlertAcknowledgementTeamId: {
                title: "Prescriber Portal Alert Acknowledgement Team",
                group: "Prescriber Portal",
                type: "team",
                help: "Team assigned the task created when a portal user acknowledges an on-hold alert. The task carries the acknowledging user's note. When no team is selected the task is created unassigned.",
            },
            frontEndInactivityTimeoutMinutes: {
                title: "Front End Inactivity Timeout (Minutes)",
                group: "Security",
                type: "number",
                min: 0,
                max: 1440,
                help: "Idle time before the app warns the user, locks the screen, and signs them out. A countdown warning appears shortly before locking, and the timer is shared across all of the user's open browser tabs. Set to 0 to disable the inactivity timeout entirely. Max 1440 (24 hours).",
            },
        };
    }

    /** Pre-computed lowercase search index keyed by field name — rebuilt only when fields change. */
    get searchIndex(): Record<string, string> {
        const index: Record<string, string> = {};
        const allFields = this.fields;
        Object.keys(allFields).forEach(f => {
            const data = allFields[f];
            const groups = data.group ? [data.group] : (Array.isArray(data.groups) ? data.groups : (data.groups ? [data.groups] : []));
            index[f] = [this.formatConfigName(f), data.help ?? "", ...groups, ...(data.keywords ?? [])].join(" ").toLowerCase();
        });
        return index;
    }

    get filteredFields() {
        if (!this.isFiltering) return this.fields;
        const fields: any = {};
        const terms = this.query.toLowerCase().split(/\s+/).filter(Boolean);
        const index = this.searchIndex;

        Object.keys(this.fields).forEach(f => {
            if (terms.every(t => index[f]?.includes(t))) fields[f] = this.fields[f];
        });

        return fields;
    }

    private openGroups: Array<string> = [];
    private query: string = "";
    private selectedGroup: string | null = null;

    // noinspection JSUnusedGlobalSymbols
    mounted() {
        this.fetchConfiguration();
        this.fetchTaskTemplates();
        this.fetchPatientAppDeliverySurveyQuestionSets();
        this.fetchPatientAppPriceSchedules();
        // Initialize with All Groups selected
        this.selectedGroup = null;

        // Set sidebar height based on its Y position
        this.updateSidebarHeight();
        window.addEventListener('resize', this.updateSidebarHeight);
        window.addEventListener('scroll', this.updateSidebarHeight);

        const search = this.$route.query.search;
        if (search) this.query = Array.isArray(search) ? (search[0] ?? '') : search;
    }

    beforeDestroy() {
        window.removeEventListener('resize', this.updateSidebarHeight);
        window.removeEventListener('scroll', this.updateSidebarHeight);
    }

    updateSidebarHeight() {
        this.$nextTick(() => {
            const sidebar = this.$el.querySelector('.config-sidebar-col') as HTMLElement;
            if (sidebar) {
                const rect = sidebar.getBoundingClientRect();
                const yPosition = rect.top;
                const newHeight = Math.min(window.innerHeight - yPosition, window.innerHeight);
                sidebar.style.height = `${Math.max(newHeight, 0)}px`;
            }
        });
    }

    selectGroup(group: string) {
        this.selectedGroup = group;
    }

    selectAllGroups() {
        this.selectedGroup = null;
    }

    @Watch("selectedGroup")
    onSelectedGroupChanged() {
        this.$nextTick(() => {
            const page = this.$el as HTMLElement;
            if (page) page.scrollIntoView({ behavior: 'smooth' });
        });
    }

    get isFiltering() {
        return this.query?.trim();
    }

    get configKeys() {
        return Object.keys(this.filteredFields)
            .filter(i => this.filteredFields[i]?.hidden !== true);
    }

    get configKeysGrid() {
        return this.configKeys.filter(i => !this.filteredFields[i]?.fullWidth);
    }

    get configKeysFullWidth() {
        return this.configKeys.filter(i => this.filteredFields[i]?.fullWidth);
    }

    get canSave() {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Admin/Configuration".toLowerCase());
    }

    get isAllGroupsSelected() {
        return this.selectedGroup === null;
    }

    get groupSelectOptions() {
        return [
            { value: null, text: 'All Groups' },
            ...this.configurationGroups.map(group => ({ value: group, text: group }))
        ];
    }

    get displayedGroups() {
        if (this.isFiltering) {
            // When filtering, show only groups that have matching configurations
            const groupsWithMatches = new Set<string>();
            Object.keys(this.filteredFields).forEach(key => {
                this.getGroupsForKey(key).forEach(group => {
                    groupsWithMatches.add(group);
                });
            });
            return Array.from(groupsWithMatches).sort();
        }
        return this.configurationGroups;
    }

    formatConfigName(name: string): string {
        const explicitTitle = this.fields[name]?.title;
        if (explicitTitle) return explicitTitle;

        let resp = name.replace(/([A-Z])/g, " $1");
        resp = resp.charAt(0).toUpperCase() + resp.slice(1);
        resp = resp.replace(" Api ", " API ");

        return resp;
    }

    getConfigType(name: string): string {
        const explicitType = this.filteredFields[name]?.type;
        if (explicitType) return explicitType as string;

        return this.configuration.typeOf(name);
    }

    async fetchConfiguration() {
        try {
            this.loading = true;
            const fetchResponse = await axios.get<Configuration>("/api/Configuration");
            this.configuration = new Configuration(fetchResponse.data);
            this.arGateWasOpen = this.arGateOpen;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }
    }

    async saveConfiguration() {
        try {
            this.loading = true;
            if (!this.configuration.autoRefillNextProcessDateDefaultCadence) {
                this.configuration.autoRefillNextProcessDateDefaultCadence = null;
            }
            this.cleanConfigurationValues();

            this.configuration.reversalQueueDays ??= 10;
            if (this.configuration.reversalQueueDays < 0) {
                this.configuration.reversalQueueDays = 0;
            }
            // Cleared number fields emit null, and this one is a required integer on the API.
            this.configuration.activityFactorDecimalPlaces ??= 4;

            const wasOpen = this.arGateWasOpen;
            const saveResponse = await axios.post<Configuration>("/Configuration", this.configuration);
            this.configuration = new Configuration(saveResponse.data);
            this.$notification(NotificationOptions.successSaveNotificationPreset(entityName));

            if (this.arGateOpen && !wasOpen) {
                this.$notification(NotificationOptions.warning(
                    "AR is enabled but no Statement Closing Day is set — statements and auto-payments will not run."));
            }
            this.arGateWasOpen = this.arGateOpen;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }
    }

    /** AR is on with no statement cycle set, which leaves both AR workers dormant. */
    get arGateOpen(): boolean {
        return !!this.configuration.enableAR && this.configuration.arStatementClosingDay == null;
    }

    /** No acceptable assay / LOD bound is set, so activity factor warnings would never fire. */
    get activityFactorRangesAllBlank(): boolean {
        const c = this.configuration;
        return c.activityFactorAssayMinPercent == null && c.activityFactorAssayMaxPercent == null
            && c.activityFactorLodMinPercent == null && c.activityFactorLodMaxPercent == null;
    }

    cleanConfigurationValues() {
        Object.keys(this.fields).forEach(k => {
            //Remove any empty strings from string array configs
            if (this.fields[k].type === "string-array") {
                this.configuration[k] = this.configuration[k]?.filter((v: string) => v.trim());
            }
            //Drop uncommitted/empty rows from number array configs so they don't serialize as null
            if (this.fields[k].type === "number-array") {
                this.configuration[k] = this.configuration[k]
                    ?.filter((v: any) => v !== null && v !== undefined && v !== "")
                    .map((v: any) => Number(v))
                    .filter((v: number) => Number.isFinite(v));
            }
        });
    }

    getConfigValue(name: string): { value: any } {
        if (name === 'patientDashboardConfiguration') {
            return {
                value: {
                    defaultLayout: this.configuration.patientDashboardDefaultLayout,
                    hiddenCards: this.configuration.patientDashboardHiddenCards
                }
            };
        }
        return { value: this.configuration[name] };
    }

    setConfigValue(name: string, value: any) {
        if (name === 'patientAppDeliverySurveyQuestionSetId' && value === '__manage__') {
            // Sentinel value: open the question-set management modal instead of saving the value.
            // The b-select's v-model snaps back to the prior value on the next render because
            // we don't mutate this.configuration here.
            this.$bvModal.show('patientAppDeliverySurveyQuestionSetsModal');
            return;
        }
        if (name === 'enableAR' && value && !this.configuration.enableAR) {
            // Read before the assignment below so this only fires on the off->on transition.
            // Enabling AR with no cycle configured leaves both AR workers dormant.
            this.configuration.arStatementClosingDay ??= 1;
        }
        if (name === 'enableActivityFactorAdjustment' && value && !this.configuration.enableActivityFactorAdjustment
            && this.activityFactorRangesAllBlank) {
            // Off->on transition only. Tenants that predate these settings carry blank ranges, which would
            // mean "never warn"; seed the documented defaults so the feature starts with warnings. Only when
            // all four are blank, so a bound someone deliberately cleared is not resurrected.
            this.configuration.activityFactorAssayMinPercent = 90;
            this.configuration.activityFactorAssayMaxPercent = 110;
            this.configuration.activityFactorLodMinPercent = 0;
            this.configuration.activityFactorLodMaxPercent = 10;
        }
        if (name === 'patientDashboardConfiguration') {
            this.configuration.patientDashboardDefaultLayout = value?.defaultLayout ?? null;
            this.configuration.patientDashboardHiddenCards = value?.hiddenCards ?? null;
        } else {
            this.configuration[name] = value;
        }
    }

    /**
     * Cascade rule: if a carrier isn't in <c>allowedCarriers</c>, disable that carrier's
     * tab on the service-types and add-ons fields. Empty/null allowedCarriers = "all
     * permitted" (matches the defaultAllChecked semantic) so nothing is disabled. Returns
     * an empty array for fields that aren't carrier-grouped.
     */
    getDisabledGroups(name: string): string[] {
        const fieldDef = this.fields[name];
        if (fieldDef?.type !== "carrier-service-checkbox") return [];
        const groups = fieldDef.serviceGroups as Record<string, readonly string[]> | undefined;
        if (!groups) return [];
        const allowed = this.configuration.allowedCarriers;
        if (!allowed || allowed.length === 0) return [];
        const allowedSet = new Set(allowed.map(c => c.toLowerCase()));
        return Object.keys(groups).filter(carrier => !allowedSet.has(carrier.toLowerCase()));
    }

    getGroupsForKey(key: string): string[] {
        const fieldDef = this.filteredFields[key];
        if (!fieldDef) return ["Other"];
        if (fieldDef.group) return [fieldDef.group];
        if (fieldDef.groups) return Array.isArray(fieldDef.groups) ? fieldDef.groups : [fieldDef.groups];
        if (Array.isArray(fieldDef)) return fieldDef;
        return [fieldDef];
    }

    get configurationGroups() {
        const groups = new Set(this.configKeys.flatMap(i => this.getGroupsForKey(i)));
        if (this.selectedGroup) groups.add(this.selectedGroup);
        return [...groups].toSorted();
    }

    /** Builds field-to-groups mapping for all fields (used in import requests) */
    get fieldGroupsMapping(): Record<string, string[]> {
        const mapping: Record<string, string[]> = {};
        for (const key of Object.keys(this.fields)) {
            const fieldDef = this.fields[key];
            if (fieldDef?.hidden) continue;
            if (fieldDef?.group) {
                mapping[key] = [fieldDef.group];
            } else if (fieldDef?.groups) {
                // Handle both string and array for groups
                mapping[key] = Array.isArray(fieldDef.groups) ? fieldDef.groups : [fieldDef.groups];
            } else {
                mapping[key] = ["Other"];
            }
        }
        return mapping;
    }

    getKeysForGroup(group: string, fullWidth: boolean) {
        if (fullWidth) {
            return this.configKeysFullWidth.filter(i => this.getGroupsForKey(i).includes(group));
        }
        return this.configKeysGrid.filter(i => this.getGroupsForKey(i).includes(group));
    }

    getFieldCountForGroup(group: string): number {
        return this.configKeys.filter(key => this.getGroupsForKey(key).includes(group)).length;
    }

    toggleGroup(group: string) {
        if (this.isFiltering) return;
        if (this.openGroups.includes(group)) {
            this.openGroups = this.openGroups.filter(og => og !== group);
        } else {
            this.openGroups.push(group);
        }
    }

    toggleGroups() {
        if (this.openGroups.length) {
            this.openGroups = [];
        } else {
            this.openGroups = [...this.configurationGroups];
        }
    }

    async fetchTaskTemplates() {
        try {
            const fetchResponse = await axios.get<UserTask[]>("/api/Task/templates");
            this.taskTemplates = fetchResponse.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {

        }
    }

    async fetchPatientAppDeliverySurveyQuestionSets() {
        try {
            const resp = await axios.get<any[]>(
                `/api/Task/question-sets/${EntityType.PatientAppDeliverySurvey}`);
            this.patientAppDeliverySurveyQuestionSets = (resp.data ?? [])
                .filter((qs: any) => qs.active !== false);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async fetchPatientAppPriceSchedules() {
        try {
            const resp = await axios.get<any[]>('/api/PriceSchedule');
            this.patientAppPriceSchedules = (resp.data ?? [])
                .map((ps: any) => ({ id: ps.id, name: ps.name }));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    // Export/Import computed properties
    get canApplyImport(): boolean {
        return this.importPreview?.canApply === true && this.selectedGroupsToImport.length > 0;
    }

    // Export/Import methods
    async exportConfiguration(bvModalEvt: Event) {
        bvModalEvt.preventDefault();
        try {
            this.exporting = true;
            const params = this.exportDescription ? { description: this.exportDescription } : {};
            const response = await axios.get("/api/Configuration/export", {
                params,
                responseType: "blob",
            });

            // Create download link
            const blob = new Blob([response.data], { type: "application/json" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            // Get filename from Content-Disposition header or use default
            const contentDisposition = response.headers["content-disposition"];
            let filename = `configuration-export-${new Date().toISOString().slice(0, 10)}.json`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, "");
                }
            }

            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            this.showExportModal = false;
            this.exportDescription = "";
            this.$notification(NotificationOptions.successSaveNotificationPreset("Configuration Export"));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.exporting = false;
        }
    }

    async previewImport() {
        if (!this.importFile) return;

        this.importError = "";
        this.importPreview = null;
        this.importData = null;

        try {
            const text = await this.importFile.text();
            const data = JSON.parse(text) as ConfigurationExport;

            // Validate the structure
            if (!data.metadata || !data.configuration) {
                this.importError = "Invalid configuration file format. Missing metadata or configuration.";
                return;
            }

            this.importData = data;

            // Get preview from server
            const request: ConfigurationImportRequest = {
                exportData: data,
                previewOnly: true,
                fieldGroups: this.fieldGroupsMapping,
            };

            const response = await axios.post<ImportPreviewType>("/api/Configuration/import/preview", request);
            this.importPreview = response.data;

            // Pre-select all groups that have changes
            this.selectedGroupsToImport = Object.keys(this.importPreview.changeCountsByGroup);
        } catch (err) {
            if (err instanceof SyntaxError) {
                this.importError = "Invalid JSON file. Please select a valid configuration export file.";
            } else {
                this.$notification(NotificationOptions.error(err));
                this.importError = "Failed to preview import. Please try again.";
            }
        }
    }

    async applyImport(bvModalEvt: Event) {
        bvModalEvt.preventDefault();
        if (!this.importData || !this.canApplyImport) return;

        try {
            this.importing = true;

            const request: ConfigurationImportRequest = {
                exportData: this.importData,
                groupsToImport: this.selectedGroupsToImport,
                previewOnly: false,
                fieldGroups: this.fieldGroupsMapping,
            };

            const response = await axios.post<Configuration>("/api/Configuration/import/apply", request);
            this.configuration = new Configuration(response.data);
            this.arGateWasOpen = this.arGateOpen;

            this.showImportModal = false;
            this.resetImport();
            this.$notification(NotificationOptions.successSaveNotificationPreset("Configuration Import"));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.importing = false;
        }
    }

    resetImport() {
        this.importFile = null;
        this.importData = null;
        this.importPreview = null;
        this.importError = "";
        this.selectedGroupsToImport = [];
    }
}
</script>

<style scoped>
.configuration-page {
    min-height: calc(100vh - 120px);
    container-type: inline-size;
}

.config-sidebar-col {
    background-color: #f8f9fa;
    border-right: 1px solid #dee2e6;
    width: 260px;
    min-width: 260px;
    flex: 0 0 260px;
    position: sticky;
    top: 0;
}

.config-sidebar {
    padding: 1rem;
    height: 100%;
    overflow-y: auto;
}

.sidebar-header-container {
    position: sticky;
    top: -1rem;
    z-index: 20;
    padding: 1rem;
    margin: -1rem -1rem 1rem -1rem;
}

.sidebar-title {
    margin: 0 0 0.5rem 0;
    font-weight: 600;
    color: #495057;
}

.search-section {
    margin-bottom: 0;
    padding-bottom: 0;
}

.search-section input {
    background-color: rgba(255, 255, 255, 0.9);
}

.groups-section {
}

.group-item {
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.125rem;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.group-name {
    flex: 1;
}

.group-item:hover {
    background-color: #e9ecef;
}

.group-item.active {
    background-color: #007bff;
    color: white;
}

.main-content-col {
    padding: 0;
    flex: 1;
    min-width: 0;
}

.config-content {
    padding: 1rem;
    background-color: white;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
}

.config-content > .actions {
    margin-top: auto;
}


.group-header {
    position: sticky;
    top: 0;
    z-index: 10;
    font-weight: bold;
    margin: 0;
    padding: 0.75rem 1rem;
}

.group-header.collapsible {
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;
}

.group-header.collapsible:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.group-header i {
    margin-right: 0.5rem;
    transition: transform 0.2s ease;
}

.group-content {
    margin-bottom: 1.5rem;
    padding: 0 1rem;
}

.top-search-section {
    display: none;
    position: sticky;
    top: 0;
    z-index: 15;
    padding: 0.75rem 1rem;
    margin: -1rem -1rem 1rem -1rem;
}

.top-search-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 0.75rem;
}

.top-search-section input,
.top-search-section select {
    background-color: rgba(255, 255, 255, 0.9);
}

.top-search-section input {
    height: 38px;
}

.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
    gap: 15px;
    margin-top: 10px;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity .2s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.actions {
    position: sticky;
    bottom: 0;
    z-index: 900;
}


/* Responsive adjustments */
@container (max-width: 1049px) {
    .config-sidebar-col {
        display: none;
    }

    .top-search-section {
        display: block;
    }
}

@media screen and (max-width: 768px) {
    .configuration-page {
        height: auto;
        overflow: visible;
    }

    .main-content-col {
        height: auto;
        overflow: visible;
    }

    .config-card {
        height: auto;
    }

    .config-card .card-body {
        overflow: visible;
    }
}
</style>
